import React, { useState, useEffect, useRef, useMemo, useCallback, useReducer } from 'react';
import { motion } from 'framer-motion';
import VirtualizedMessageList from './VirtualizedMessageList';
import offlineStorage from '../utils/offlineStorage';
import { useWebWorker } from '../hooks/useWebWorker';
import chatCache from '../utils/cache';
import useOfflineStatus from '../hooks/useOfflineStatus';
import useAuthStore from '../store/authStore';
import { useDarkMode } from '../contexts/DarkModeContext';
import { db, storage } from '../firebase';
import ProductModal from './ProductModal';
import DOMPurify from 'dompurify';
import CryptoJS from 'crypto-js';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  getDocs,
  limit,
  startAfter,
  deleteDoc,
  arrayUnion,
  where,
  limitToLast
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { FaTrash, FaPaperclip, FaTimes, FaRegSmile } from 'react-icons/fa';
import useUserOnlineStatus from '../hooks/useUserOnlineStatus';
import { useSocket } from '../hooks/useSocket';
import { toast } from 'react-toastify';
import LazyImage from './LazyImage';
import useLazyMessages from '../hooks/useLazyMessages';
import SecurityMonitor from './SecurityMonitor';
import MessageBubble from './MessageBubble';
import InputArea from './InputArea';
import MessageContextMenu from './MessageContextMenu';

// ⚡️ TIMESTAMP NORMALIZATION: Handle different timestamp formats consistently
function tsToMs(t) {
  if (!t) return 0;
  if (typeof t.toDate === 'function') return t.toDate().getTime(); // Firestore Timestamp
  if (t instanceof Date) return t.getTime();                       // JS Date
  if (typeof t === 'number') return t;                             // already ms
  if (typeof t.seconds === 'number') return t.seconds * 1000;      // {seconds, nanoseconds}
  return 0;
}

// Security: Enhanced file type validation constants
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/ogg'
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_MESSAGE_LENGTH = 1000; // Maximum message length
const RATE_LIMIT_WINDOW = 60000; // 1 minute
const MAX_MESSAGES_PER_MINUTE = 50; // Increased from 10 to 50 messages per minute

// Security: Rate limiting state
const rateLimitMap = new Map();
const fileUploadRateLimitMap = new Map();
const FILE_UPLOAD_LIMIT = 3; // 3 files per minute
const FILE_UPLOAD_WINDOW = 60000; // 1 minute

// Security: Enhanced file validation with additional checks
const validateFile = (file) => {
  if (!file) return { isValid: false, error: 'No file provided' };
  
  // Check file type
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return { 
      isValid: false, 
      error: 'Invalid file type. Only images (JPEG, PNG, GIF, WebP) and videos (MP4, WebM, OGG) are allowed.' 
    };
  }
  
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { 
      isValid: false, 
      error: 'File size too large. Maximum size is 5MB.' 
    };
  }
  
  // Check file name for malicious extensions
  const fileName = file.name.toLowerCase();
  const dangerousExtensions = ['.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar'];
  if (dangerousExtensions.some(ext => fileName.includes(ext))) {
    return { 
      isValid: false, 
      error: 'Dangerous file type detected. Upload blocked for security.' 
    };
  }
  
  // Validate file content (basic check)
  if (file.size === 0) {
    return { 
      isValid: false, 
      error: 'Empty file detected.' 
    };
  }
  
  return { isValid: true, error: null };
};

// Security: Enhanced text sanitization with additional protection
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  // Remove null bytes and control characters
  let sanitized = text.replace(/[\x00-\x1F\x7F]/g, '');
  
  // Remove potential script injection patterns
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/data:/gi, '');
  sanitized = sanitized.replace(/vbscript:/gi, '');
  
  // Apply DOMPurify with strict settings
  return DOMPurify.sanitize(sanitized, { 
    ALLOWED_TAGS: [], 
    ALLOWED_ATTR: [],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
  });
};

// Security: Rate limiting function
const checkRateLimit = (userId) => {
  const now = Date.now();
  const userRateLimit = rateLimitMap.get(userId) || { count: 0, resetTime: now + RATE_LIMIT_WINDOW };
  
  if (now > userRateLimit.resetTime) {
    userRateLimit.count = 1;
    userRateLimit.resetTime = now + RATE_LIMIT_WINDOW;
  } else {
    userRateLimit.count++;
  }
  
  rateLimitMap.set(userId, userRateLimit);
  
  if (userRateLimit.count > MAX_MESSAGES_PER_MINUTE) {
    return { allowed: false, remainingTime: userRateLimit.resetTime - now };
  }
  
  return { allowed: true, remainingMessages: MAX_MESSAGES_PER_MINUTE - userRateLimit.count };
};

// Security: Enhanced file upload rate limiting with server-side tracking
const checkFileUploadRateLimit = async (userId) => {
  const now = Date.now();
  
  try {
    // Check server-side rate limit
    const rateLimitRef = doc(db, 'uploadRateLimits', userId);
    const rateLimitDoc = await getDoc(rateLimitRef);
    
    let userFileRateLimit;
    if (rateLimitDoc.exists()) {
      const data = rateLimitDoc.data();
      userFileRateLimit = {
        count: data.count || 0,
        resetTime: data.resetTime || now + FILE_UPLOAD_WINDOW,
        lastUploadTime: data.lastUploadTime || 0
      };
    } else {
      userFileRateLimit = {
        count: 0,
        resetTime: now + FILE_UPLOAD_WINDOW,
        lastUploadTime: 0
      };
    }
    
    // Check if window has reset
    if (now > userFileRateLimit.resetTime) {
      userFileRateLimit.count = 1;
      userFileRateLimit.resetTime = now + FILE_UPLOAD_WINDOW;
    } else {
      userFileRateLimit.count++;
    }
    
    // Update server-side rate limit
    await setDoc(rateLimitRef, {
      userId,
      count: userFileRateLimit.count,
      resetTime: userFileRateLimit.resetTime,
      lastUploadTime: now,
      updatedAt: serverTimestamp()
    });
    
    if (userFileRateLimit.count > FILE_UPLOAD_LIMIT) {
      logSecurityEvent('UPLOAD_RATE_LIMIT_EXCEEDED', {
        userId,
        count: userFileRateLimit.count,
        limit: FILE_UPLOAD_LIMIT
      }, 'warning');
      
      return { allowed: false, remainingTime: userFileRateLimit.resetTime - now };
    }
    
    return { allowed: true, remainingUploads: FILE_UPLOAD_LIMIT - userFileRateLimit.count };
  } catch (error) {
    // Fallback to client-side rate limiting if server fails
    logSecurityEvent('UPLOAD_RATE_LIMIT_FALLBACK', { error: error.message }, 'warning');
    
    const userFileRateLimit = fileUploadRateLimitMap.get(userId) || { 
      count: 0, 
      resetTime: now + FILE_UPLOAD_WINDOW 
    };
    
    if (now > userFileRateLimit.resetTime) {
      userFileRateLimit.count = 1;
      userFileRateLimit.resetTime = now + FILE_UPLOAD_WINDOW;
    } else {
      userFileRateLimit.count++;
    }
    
    fileUploadRateLimitMap.set(userId, userFileRateLimit);
    
    if (userFileRateLimit.count > FILE_UPLOAD_LIMIT) {
      return { allowed: false, remainingTime: userFileRateLimit.resetTime - now };
    }
    
    return { allowed: true, remainingUploads: FILE_UPLOAD_LIMIT - userFileRateLimit.count };
  }
};

// Security: Input validation function
const validateInput = (input) => {
  if (!input || typeof input !== 'string') return { isValid: false, error: 'Invalid input' };
  
  const trimmed = input.trim();
  if (trimmed.length === 0) return { isValid: false, error: 'Message cannot be empty' };
  if (trimmed.length > MAX_MESSAGE_LENGTH) return { isValid: false, error: `Message too long. Maximum ${MAX_MESSAGE_LENGTH} characters.` };
  
  // Check for potential injection patterns
  const injectionPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /data:text\/html/gi,
    /vbscript:/gi,
    /on\w+\s*=/gi
  ];
  
  for (const pattern of injectionPatterns) {
    if (pattern.test(trimmed)) {
      return { isValid: false, error: 'Potentially malicious content detected' };
    }
  }
  
  return { isValid: true, error: null };
};

// Security: Emoji validation
const validateEmoji = (emoji) => {
  if (!emoji || typeof emoji !== 'string') return false;
  
  // Simple validation: allow common emoji characters
  // Check if the emoji contains only valid Unicode emoji characters
  const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1F018}-\u{1F270}\u{238C}-\u{23CF}\u{23E9}-\u{23F3}\u{23F8}-\u{23FA}]/u;
  
  // Also allow basic emoticons and symbols
  const basicEmojiRegex = /[😀-🙏🌀-🗿🚀-🛿🈁-🈂️⭐-⭕]/u;
  
  return emojiRegex.test(emoji) || basicEmojiRegex.test(emoji) || emoji.length <= 4;
};

// Security: Encryption functions
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'default-key-change-in-production';

const encryptSensitiveData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    return CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
  } catch (error) {
    console.error('Encryption error:', error);
    return null;
  }
};

const decryptSensitiveData = (encryptedData) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

// Security: Audit logging
const logSecurityEvent = (event, details, severity = 'info') => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    event,
    details,
    severity,
    userId: useAuthStore.getState().user?.uid || 'unknown',
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  // In production, send to secure logging service
  console.log('SECURITY_LOG:', logEntry);
  
  // Store in localStorage for debugging (remove in production)
  const logs = JSON.parse(localStorage.getItem('securityLogs') || '[]');
  logs.push(logEntry);
  if (logs.length > 100) logs.shift(); // Keep only last 100 logs
  localStorage.setItem('securityLogs', JSON.stringify(logs));
};

// Security: Generate CSRF token
const generateCSRFToken = () => {
  return CryptoJS.lib.WordArray.random(32).toString();
};

// Security: CSRF token storage
let csrfToken = generateCSRFToken();

// ⚡️ PERFORMANCE OPTIMIZATION: State reducer for better state management
const initialState = {
  input: '',
  showEmojiPicker: false,
  sending: false,
  loadingMore: false,
  showTick: false,
  firestoreLoading: false,
  uploadingFile: null,
  uploading: false,
  uploadProgress: 0,
  uploadedFileUrl: null,
  openedImage: null,
  readMessages: {},
  messageStatus: {},
  loginInfo: '',
  savingLoginInfo: false,
  showProductModal: false,
  isDragOver: false,
  pendingMessages: [],
  lastVisible: null,
  hasMore: true,
  firestoreMessages: [],
  localFirestoreConvId: null
};

const chatReducer = (state, action) => {
  switch (action.type) {
    case 'SET_INPUT':
      return { ...state, input: action.payload };
    case 'TOGGLE_EMOJI_PICKER':
      return { ...state, showEmojiPicker: !state.showEmojiPicker };
    case 'SET_SENDING':
      return { ...state, sending: action.payload };
    case 'SET_LOADING_MORE':
      return { ...state, loadingMore: action.payload };
    case 'SET_SHOW_TICK':
      return { ...state, showTick: action.payload };
    case 'SET_FIRESTORE_LOADING':
      return { ...state, firestoreLoading: action.payload };
    case 'SET_UPLOADING_FILE':
      return { ...state, uploadingFile: action.payload };
    case 'SET_UPLOADING':
      return { ...state, uploading: action.payload };
    case 'SET_UPLOAD_PROGRESS':
      return { ...state, uploadProgress: action.payload };
    case 'SET_UPLOADED_FILE_URL':
      return { ...state, uploadedFileUrl: action.payload };
    case 'SET_OPENED_IMAGE':
      return { ...state, openedImage: action.payload };
    case 'UPDATE_READ_MESSAGES':
      return { 
        ...state, 
        readMessages: { ...state.readMessages, ...action.payload }
      };
    case 'UPDATE_MESSAGE_STATUS':
      return { 
        ...state, 
        messageStatus: { ...state.messageStatus, ...action.payload }
      };
    case 'SET_LOGIN_INFO':
      return { ...state, loginInfo: action.payload };
    case 'SET_SAVING_LOGIN_INFO':
      return { ...state, savingLoginInfo: action.payload };
    case 'SET_SHOW_PRODUCT_MODAL':
      return { ...state, showProductModal: action.payload };
    case 'SET_DRAG_OVER':
      return { ...state, isDragOver: action.payload };
    case 'ADD_PENDING_MESSAGE':
      return { 
        ...state, 
        pendingMessages: [...state.pendingMessages, action.payload]
      };
    case 'UPDATE_PENDING_MESSAGES':
      return { ...state, pendingMessages: action.payload };
    case 'REPLACE_PENDING_MESSAGE':
      return {
        ...state,
        pendingMessages: state.pendingMessages.map(msg => 
          msg.id === action.payload.tempId 
            ? action.payload.realMessage 
            : msg
        )
      };
    case 'UPDATE_PENDING_MESSAGE':
      return {
        ...state,
        pendingMessages: state.pendingMessages.map(msg => 
          msg.id === action.payload.id 
            ? { ...msg, status: action.payload.status, error: action.payload.error }
            : msg
        )
      };
    case 'SET_LAST_VISIBLE':
      return { ...state, lastVisible: action.payload };
    case 'SET_HAS_MORE':
      return { ...state, hasMore: action.payload };
    case 'SET_FIRESTORE_MESSAGES':
      return { ...state, firestoreMessages: action.payload };
    case 'SET_LOCAL_FIRESTORE_CONV_ID':
      return { ...state, localFirestoreConvId: action.payload };
    case 'RESET_UPLOAD_STATE':
      return { 
        ...state, 
        uploadingFile: null,
        uploading: false,
        uploadProgress: 0,
        uploadedFileUrl: null
      };
    default:
      return state;
  }
};

// Security: Enhanced message type validation with integrity verification
const validateMessageType = (msg) => {
  // Validate special message types with strict schema checking
  if (msg.type === 'order') {
    // Validate order message structure
    if (!msg.order_id || typeof msg.order_id !== 'string') {
      logSecurityEvent('INVALID_ORDER_MESSAGE', { 
        hasOrderId: !!msg.order_id, 
        orderIdType: typeof msg.order_id 
      }, 'warning');
      return false;
    }
    
    // Validate order message has required fields
    const requiredOrderFields = ['order_id', 'product_name', 'amount'];
    for (const field of requiredOrderFields) {
      if (!msg[field]) {
        logSecurityEvent('MISSING_ORDER_FIELD', { field }, 'warning');
        return false;
      }
    }
    
    return true;
  }
  
  if (msg.type === 'login_info') {
    // Block plain text login info - should be encrypted
    logSecurityEvent('PLAINTEXT_LOGIN_INFO_BLOCKED', { 
      messageLength: msg.text?.length || 0 
    }, 'error');
    return false;
  }
  
  if (msg.type === 'file') {
    // Validate file message structure
    if (!msg.url || !msg.fileType) {
      logSecurityEvent('INVALID_FILE_MESSAGE', { 
        hasUrl: !!msg.url, 
        hasFileType: !!msg.fileType 
      }, 'warning');
      return false;
    }
    
    return true;
  }
  
  // Default text message validation
  return true;
};

// Security: Enhanced message integrity verification with visual indicators
const verifyMessageIntegrity = (msg) => {
  if (!msg.integrityHash) {
    logSecurityEvent('MESSAGE_INTEGRITY_MISSING', { 
      messageId: msg.id,
      messageType: msg.type 
    }, 'warning');
    return { isValid: false, reason: 'missing_hash' };
  }
  
  // Recalculate expected hash
  const expectedHash = CryptoJS.SHA256(
    `${msg.text || ''}${msg.from || ''}${msg.timestamp || Date.now()}`
  ).toString();
  
  if (msg.integrityHash !== expectedHash) {
    logSecurityEvent('MESSAGE_INTEGRITY_FAILED', { 
      messageId: msg.id,
      expectedHash: expectedHash.substring(0, 8) + '...',
      receivedHash: msg.integrityHash.substring(0, 8) + '...'
    }, 'error');
    return { isValid: false, reason: 'hash_mismatch' };
  }
  
  return { isValid: true, reason: 'valid' };
};

// Security: Enhanced socket message validation with CSRF protection and type validation
const validateSocketMessage = (msg) => {
  if (!msg || typeof msg !== 'object') {
    logSecurityEvent('INVALID_SOCKET_MESSAGE', { message: 'Non-object message received' }, 'warning');
    return null;
  }
  
  // Validate required fields
  if (!msg.from || !msg.text) {
    logSecurityEvent('INVALID_SOCKET_MESSAGE', { message: 'Missing required fields' }, 'warning');
    return null;
  }
  
  // Security: Validate CSRF token if present
  if (msg.csrfToken && msg.csrfToken !== csrfToken) {
    logSecurityEvent('CSRF_TOKEN_MISMATCH', { 
      receivedToken: msg.csrfToken?.substring(0, 8) + '...',
      expectedToken: csrfToken.substring(0, 8) + '...'
    }, 'error');
    return null;
  }
  
  // Security: Validate message type and structure
  if (!validateMessageType(msg)) {
    logSecurityEvent('INVALID_MESSAGE_TYPE', { 
      messageType: msg.type,
      messageId: msg.id 
    }, 'warning');
    return null;
  }
  
  // Security: Verify message integrity with enhanced feedback
  const integrityCheck = verifyMessageIntegrity(msg);
  if (!integrityCheck.isValid) {
    logSecurityEvent('MESSAGE_INTEGRITY_VERIFICATION_FAILED', { 
      messageType: msg.type,
      messageId: msg.id,
      reason: integrityCheck.reason
    }, 'error');
    
    // Add visual indicator for tampered messages
    sanitizedMsg.isTampered = true;
    sanitizedMsg.tamperReason = integrityCheck.reason;
  }
  
  // Sanitize message content
  const sanitizedMsg = {
    ...msg,
    text: sanitizeText(msg.text),
    from: sanitizeText(msg.from)
  };
  
  // Validate message length
  if (sanitizedMsg.text.length > MAX_MESSAGE_LENGTH) {
    logSecurityEvent('MESSAGE_TOO_LONG', { length: sanitizedMsg.text.length }, 'warning');
    return null;
  }
  
  // Add message integrity hash
  sanitizedMsg.integrityHash = CryptoJS.SHA256(
    `${sanitizedMsg.text}${sanitizedMsg.from}${sanitizedMsg.timestamp || Date.now()}`
  ).toString();
  
  return sanitizedMsg;
};

// Add this new component at the top of the file, before the main ChatPanel component
function CountdownTimer({ startTime, orderId, conversationId, onStatusUpdate, isBuyer, status, category }) {
  const [timeLeft, setTimeLeft] = useState({
    days: 7,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isComplete, setIsComplete] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(status === 'completed');
  const [isAutoCompleted, setIsAutoCompleted] = useState(false);

  const isYouTube = category?.toLowerCase().includes('youtube');

  useEffect(() => {
    if (status === 'completed') {
      setIsConfirmed(true);
      return;
    }

    const endTime = new Date(startTime).getTime() + (isYouTube ? 7 : 1) * 24 * 60 * 60 * 1000;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = endTime - now;

      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsComplete(true);
        // Only auto complete for non-YouTube categories
        if (!isYouTube) {
          handleAutoComplete();
        }
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, status, isYouTube]);

  const handleAutoComplete = async () => {
    if (!orderId || !conversationId) return;
    
    try {
      // Update the conversation document
      const convRef = doc(db, 'sellerbuyerconversations', conversationId);
      await updateDoc(convRef, {
        status: 'completed'
      });

      // Update the order message
      const messagesCol = collection(db, 'sellerbuyerconversations', conversationId, 'messages');
      const q = query(messagesCol, where('order_id', '==', orderId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const messageDoc = querySnapshot.docs[0];
        await updateDoc(doc(messagesCol, messageDoc.id), {
          status: 'completed'
        });
      }

      // Update WordPress order status
      try {
        const response = await fetch('/api/update-order-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderId: orderId,
            status: 'completed'
          })
        });

        if (!response.ok) {
          throw new Error('Failed to update WordPress order status');
        }
      } catch (error) {
        console.error('Error updating WordPress order status:', error);
      }

      // Notify parent component about the status update
      if (onStatusUpdate) {
        onStatusUpdate('completed');
      }

      setIsConfirmed(true);
      setIsAutoCompleted(true);
    } catch (error) {
      console.error('Error auto-completing order:', error);
    }
  };

  const handleConfirmOrder = async () => {
    if (!orderId || !conversationId) return;
    
    setIsConfirming(true);
    try {
      // Update the conversation document
      const convRef = doc(db, 'sellerbuyerconversations', conversationId);
      await updateDoc(convRef, {
        status: 'completed'
      });

      // Update the order message
      const messagesCol = collection(db, 'sellerbuyerconversations', conversationId, 'messages');
      const q = query(messagesCol, where('order_id', '==', orderId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const messageDoc = querySnapshot.docs[0];
        await updateDoc(doc(messagesCol, messageDoc.id), {
          status: 'completed'
        });
      }

      // Update WordPress order status
      try {
        const response = await fetch('/api/update-order-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderId: orderId,
            status: 'completed'
          })
        });

        if (!response.ok) {
          throw new Error('Failed to update WordPress order status');
        }
      } catch (error) {
        console.error('Error updating WordPress order status:', error);
      }

      // Notify parent component about the status update
      if (onStatusUpdate) {
        onStatusUpdate('completed');
      }

      setIsConfirmed(true);
    } catch (error) {
      console.error('Error updating order status:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="w-full mt-4">
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg p-6 transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
        <div className="flex items-center justify-between mb-4">
          <div className="font-bold text-lg text-gray-800 flex items-center gap-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-md">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span>Confirmation Status</span>
          </div>
          {isConfirmed && (
            <span className="px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-semibold shadow-sm">
              Confirmed
            </span>
          )}
        </div>
        {isConfirmed ? (
          <div className="flex items-center justify-start gap-3 pl-2">
            <div className="flex items-center gap-3 bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 rounded-xl border border-green-200 shadow-sm w-full">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-md">
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-green-700 font-medium text-base">
                {isAutoCompleted 
                  ? "The time period has expired. The transaction is now complete automatically."
                  : "Buyer has confirmed the order. The transaction is now complete."}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm mb-4">
              <div className="flex items-center gap-4 justify-start">
                {isYouTube && (
                  <>
                    <div className="flex flex-col items-center">
                      <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl px-6 py-3 min-w-[100px] text-center shadow-md">
                        <span className="text-3xl font-bold">{String(timeLeft.days).padStart(2, '0')}</span>
                      </div>
                      <span className="text-sm text-gray-500 mt-2 font-medium">Days</span>
                    </div>
                    <span className="text-3xl font-bold text-gray-400">:</span>
                  </>
                )}
                <div className="flex flex-col items-center">
                  <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl px-6 py-3 min-w-[100px] text-center shadow-md">
                    <span className="text-3xl font-bold">{String(timeLeft.hours).padStart(2, '0')}</span>
                  </div>
                  <span className="text-sm text-gray-500 mt-2 font-medium">Hours</span>
                </div>
                <span className="text-3xl font-bold text-gray-400">:</span>
                <div className="flex flex-col items-center">
                  <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl px-6 py-3 min-w-[100px] text-center shadow-md">
                    <span className="text-3xl font-bold">{String(timeLeft.minutes).padStart(2, '0')}</span>
                  </div>
                  <span className="text-sm text-gray-500 mt-2 font-medium">Minutes</span>
                </div>
                <span className="text-3xl font-bold text-gray-400">:</span>
                <div className="flex flex-col items-center">
                  <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl px-6 py-3 min-w-[100px] text-center shadow-md">
                    <span className="text-3xl font-bold">{String(timeLeft.seconds).padStart(2, '0')}</span>
                  </div>
                  <span className="text-sm text-gray-500 mt-2 font-medium">Seconds</span>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {isYouTube 
                  ? "The account primary ownership transfer will be completed after 7 days"
                  : "Please confirm the account details within this time period"}
              </div>
            </div>
            {isBuyer && !isYouTube && (
              <div className="mt-4">
                <button
                  onClick={handleConfirmOrder}
                  disabled={isConfirming}
                  className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  {isConfirming ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Confirming...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Confirm Order
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function getStatusBadgeClass(status) {
  if (!status) return 'bg-gray-400 text-white';
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-red-500 text-white';
    case 'transferring':
      return 'bg-yellow-500 text-black';
    case 'refunded':
      return 'bg-blue-500 text-white';
    case 'cancel':
      return 'bg-black text-white';
    default:
      return 'bg-gray-400 text-white';
  }
}



export default function ChatPanel({ conversation, activeTab, moveConversationToTop, onSelectConversation, transactions = [], agentMessages = [], setAgentMessages, updateAgentChatPreview, updateMessagesPreview, messages, setMessages, isAdminDashboard, firestoreConvId, selectedConversationId }) {
  const { isDarkMode } = useDarkMode();
  const { user } = useAuthStore();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Replace multiple useState with useReducer
  const [state, dispatch] = useReducer(chatReducer, initialState);
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Web Worker for CPU-heavy tasks
  const { sanitizeText: sanitizeTextWithWorker, encryptText: encryptTextWithWorker, decryptText: decryptTextWithWorker, processMessages: processMessagesWithWorker, validateFile: validateFileWithWorker, isWorkerAvailable } = useWebWorker();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Socket message deduplication
  const seenMessageIds = useRef(new Set());
  const messageDeduplicationTimeout = useRef(new Map());
  
  // Destructure state for easier access
  const {
    input, showEmojiPicker, sending, loadingMore, showTick, firestoreLoading,
    uploadingFile, uploading, uploadProgress, uploadedFileUrl, openedImage,
    readMessages, messageStatus, loginInfo, savingLoginInfo, showProductModal,
    isDragOver, pendingMessages, lastVisible, hasMore, firestoreMessages,
    localFirestoreConvId
  } = state;
  
  const emojiPickerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const { joinRoom, sendMessage, onMessage, isConnected } = useSocket();
  const { isOnline, offlineQueue, addToOfflineQueue, getOfflineStatus } = useOfflineStatus();

  // 🎨 REPLY FUNCTIONALITY: State for reply-to message
  const [replyToMessage, setReplyToMessage] = useState(null);

  // 🎨 CONTEXT MENU: State for context menu
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    message: null
  });

  // 1. Compute variables like otherPartyUid before calling hooks.
  let otherPartyUid = '';
  if (isAdminDashboard && conversation && (conversation.uid || conversation.userUid)) {
    otherPartyUid = conversation.uid || conversation.userUid;
  } else if (user && conversation && conversation.buyer && conversation.seller) {
    if (conversation.buyer.email && conversation.buyer.email.toLowerCase() === user.email.toLowerCase()) {
      otherPartyUid = conversation.seller.uid;
    } else if (conversation.seller.email && conversation.seller.email.toLowerCase() === user.email.toLowerCase()) {
      otherPartyUid = conversation.buyer.uid;
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced memoization for message processing
  const currentMessages = useMemo(() => {
    if (!conversation) return [];
    
    // Merge pending and Firestore messages for instant rendering
    const allMessages = [...pendingMessages, ...firestoreMessages];
    
    if (conversation.id === 'admin') {
      return allMessages;
    }
    return allMessages;
  }, [conversation, firestoreMessages, pendingMessages]);

  // ⚡️ WHATSAPP-STYLE INSTANT DISPLAY: Optimized message processing (avoid sorting for optimistic messages)
  const sortedAndFilteredMessages = useMemo(() => {
    if (!currentMessages || currentMessages.length === 0) return [];
    
    // ⚡️ SEPARATE OPTIMISTIC: Don't sort optimistic messages (they're already in correct order)
    const optimisticMessages = currentMessages.filter(msg => msg.isOptimistic);
    const nonOptimisticMessages = currentMessages.filter(msg => !msg.isOptimistic);
    
    // ⚡️ DEDUPLICATION: Only for non-optimistic messages
    const uniqueNonOptimistic = nonOptimisticMessages.filter((msg, index, self) => 
      index === self.findIndex(m => 
        m.id === msg.id || 
        (m.text === msg.text && m.from === msg.from && m.timestamp === msg.timestamp)
      )
    );
    
    // ⚡️ SORTING: Only sort non-optimistic messages (optimistic are already in correct order)
    const sortedNonOptimistic = uniqueNonOptimistic.sort((a, b) => {
      const aTime = tsToMs(a.timestamp ?? a.createdAt ?? a.clientCreatedAt);
      const bTime = tsToMs(b.timestamp ?? b.createdAt ?? b.clientCreatedAt);
      return aTime - bTime; // ascending (old → new)
    });
    
    // ⚡️ INSTANT COMBINE: Existing first, then optimistic (newest at bottom)
    return [...sortedNonOptimistic, ...optimisticMessages];
  }, [currentMessages]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Lazy loading for existing messages only
  const { visibleMessages, hasMore: hasMoreLazy, isLoading: loadingMoreLazy, handleScroll } = useLazyMessages(sortedAndFilteredMessages, 30);
  
  // ⚡️ INSTANT DISPLAY: Separate pending messages for immediate rendering
  // ⚡️ WHATSAPP-STYLE INSTANT DISPLAY: Optimized pending messages (no unnecessary processing)
  const pendingMessagesForDisplay = useMemo(() => {
    // ⚡️ INSTANT PROCESSING: Only add status if missing, no other transformations
    return pendingMessages.map(msg => ({
      ...msg,
      isPending: true,
      status: msg.status || 'sending'
    }));
  }, [pendingMessages]);
  
  // ⚡️ WHATSAPP-STYLE INSTANT DISPLAY: Show existing messages first, then optimistic messages
  const allVisibleMessages = useMemo(() => {
    // ⚡️ PRIORITY 1: Show existing messages (already sorted)
    const existingMessages = visibleMessages;
    
    // ⚡️ PRIORITY 2: Show optimistic messages immediately (no sorting needed)
    const optimisticMessages = pendingMessagesForDisplay.filter(msg => msg.isOptimistic);
    
    // ⚡️ INSTANT COMBINE: Existing first, then optimistic (newest at bottom)
    return [...existingMessages, ...optimisticMessages];
  }, [pendingMessagesForDisplay, visibleMessages]);

  useEffect(() => {
    // Only mark as read if this chat panel is for the currently selected conversation
    if (
      !user ||
      !conversation ||
      !firestoreMessages.length ||
      (typeof selectedConversationId !== 'undefined' && conversation.id !== selectedConversationId)
    ) {
      return;
    }
    const unreadMessages = firestoreMessages.filter(msg =>
      !msg.readBy?.includes(user.email) &&
      msg.from !== user.email
    );
    const convId = conversation?.id || firestoreConvId || localFirestoreConvId;
    if (unreadMessages.length > 0 && convId) {
      // Mark messages as read in batches to avoid overwhelming Firestore
      const batchSize = 10;
      for (let i = 0; i < unreadMessages.length; i += batchSize) {
        const batch = unreadMessages.slice(i, i + batchSize);
        Promise.all(batch.map(msg => markMessageAsRead(msg.id, convId)))
          .then(() => {})
          .catch(error => {});
      }
    }
  }, [firestoreMessages, user, conversation, firestoreConvId, localFirestoreConvId, selectedConversationId]);

  // Add this function for marking messages as read
  const markMessageAsRead = async (messageId, conversationId) => {
    if (!user || !messageId || !conversationId) {
      return;
    }

    try {
      let messageRef;
      // Use correct collection for agent or user-to-user chat
      if (
        (conversation && conversation.id === 'admin') ||
        isAdminDashboard ||
        (conversationId && conversationId.startsWith('agent_'))
      ) {
        messageRef = doc(db, 'agentConversations', conversationId, 'messages', messageId);
      } else {
        messageRef = doc(db, 'sellerbuyerconversations', conversationId, 'messages', messageId);
      }

      // Get current message data
      const messageDoc = await getDoc(messageRef);
      if (!messageDoc.exists()) {
        return;
      }

      const messageData = messageDoc.data();
      const currentReadBy = messageData.readBy || [];
      
      // Only update if not already read by this user
      if (!currentReadBy.includes(user.email)) {
        await updateDoc(messageRef, {
          readBy: arrayUnion(user.email),
          status: 'read'
        });
      }

      dispatch({ type: 'UPDATE_MESSAGE_STATUS', payload: { [messageId]: 'read' } });
    } catch (error) {
    }
  };

  // Add this function to update message status when sending
  const updateMessageStatus = async (messageId, conversationId, status) => {
    if (!messageId || !conversationId) return;
    
    try {
      const messageRef = doc(db, 'agentConversations', conversationId, 'messages', messageId);
      await updateDoc(messageRef, {
        status: status
      });
      dispatch({ type: 'UPDATE_MESSAGE_STATUS', payload: { [messageId]: status } });
    } catch (error) {
    }
  };

  // Update the useEffect that marks messages as read so it triggers whenever the chat panel is open and new messages arrive
  useEffect(() => {
    // Only mark as read if this chat panel is for the currently selected conversation
    if (
      !user ||
      !conversation ||
      !firestoreMessages.length ||
      (typeof selectedConversationId !== 'undefined' && conversation.id !== selectedConversationId)
    ) {
      return;
    }

    const unreadMessages = firestoreMessages.filter(msg =>
      !msg.readBy?.includes(user.email) &&
      msg.from !== user.email
    );

    const convId = conversation?.id || firestoreConvId || localFirestoreConvId;
    if (unreadMessages.length > 0 && convId) {
      // Mark messages as read in batches to avoid overwhelming Firestore
      const batchSize = 10;
      for (let i = 0; i < unreadMessages.length; i += batchSize) {
        const batch = unreadMessages.slice(i, i + batchSize);
        Promise.all(batch.map(msg => markMessageAsRead(msg.id, convId)))
          .then(() => {
          })
          .catch(error => {
          });
      }
    }
  }, [firestoreMessages, user, conversation, firestoreConvId, localFirestoreConvId, selectedConversationId]);

  // --- ONLINE STATUS HOOK FIX ---
  const otherPartyOnline = useUserOnlineStatus(otherPartyUid || null);

  // Firestore: Paginated message loading
  const PAGE_SIZE = 20;

    // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced loadMessages with dual-cache system
  const loadMessages = async (convId, opts = {}) => {
    if (!convId) return;
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Check memory cache first (fastest)
    if (!opts.startAfterDoc && !opts.forceRefresh) {
      const memoryCachedMessages = chatCache.getCachedMessages(convId);
      if (memoryCachedMessages && memoryCachedMessages.length > 0) {
        console.log(`Loading ${memoryCachedMessages.length} messages from memory cache for conversation ${convId}`);
        dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: memoryCachedMessages });
        
        // Load fresh data in background (non-blocking)
        setTimeout(() => loadMessagesFromServer(convId, opts), 50);
        return;
      }
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Fallback to IndexedDB cache
      try {
        const indexedDBCachedMessages = await offlineStorage.getCachedMessages(convId);
        if (indexedDBCachedMessages && indexedDBCachedMessages.length > 0) {
          console.log(`Loading ${indexedDBCachedMessages.length} messages from IndexedDB cache for conversation ${convId}`);
          dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: indexedDBCachedMessages });
          
          // Update memory cache with IndexedDB data
          chatCache.cacheMessages(convId, indexedDBCachedMessages);
          
          // Load fresh data in background (non-blocking)
          setTimeout(() => loadMessagesFromServer(convId, opts), 100);
          return;
        }
      } catch (error) {
        console.warn('Failed to load from IndexedDB cache:', error);
      }
    }
    
    // Load from server
    await loadMessagesFromServer(convId, opts);
  };

  const loadMessagesFromServer = async (convId, opts = {}) => {
    // Only show loading state when loading more messages (scrolling up)
    if (opts.startAfterDoc) {
    dispatch({ type: 'SET_LOADING_MORE', payload: true });
    }
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Use limitToLast for faster initial load
    let q;
    if (opts.startAfterDoc) {
      // For pagination (loading older messages)
      q = query(
      collection(db, 'agentConversations', convId, 'messages'),
      orderBy('timestamp', 'desc'),
        startAfter(opts.startAfterDoc),
      limit(PAGE_SIZE)
    );
    } else {
      // ⚡️ OPTIMIZATION: Load only last 30 messages initially for faster load
      q = query(
        collection(db, 'agentConversations', convId, 'messages'),
        orderBy('timestamp', 'desc'),
        limitToLast(30) // Load only last 30 messages for instant display
      );
    }
    
    const snap = await getDocs(q);
    let msgs = snap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        time: data.timestamp ? new Date(data.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
    });
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Reverse messages for correct display order
    if (!opts.startAfterDoc) {
      msgs = msgs.reverse(); // Show newest messages at bottom
    }
    
    if (opts.append) {
      dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [...firestoreMessages, ...msgs] });
    } else {
      dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: msgs });
      // ⚡️ PERFORMANCE OPTIMIZATION: Cache the messages in both memory and IndexedDB
      try {
        // Cache in memory (fastest access)
      chatCache.cacheMessages(convId, msgs);
        // Cache in IndexedDB (persistent storage)
        await offlineStorage.cacheMessages(convId, msgs);
        console.log(`Cached ${msgs.length} messages in both memory and IndexedDB for conversation ${convId}`);
        
        // ⚡️ PERFORMANCE OPTIMIZATION: Preload next batch of messages in background
        if (msgs.length > 0 && !opts.startAfterDoc && snap.docs.length > 0) {
          setTimeout(() => {
            loadMessages(convId, { startAfterDoc: snap.docs[snap.docs.length - 1], append: true });
          }, 2000); // Preload after 2 seconds
        }
      } catch (error) {
        console.warn('Failed to cache messages:', error);
      }
    }
    
    dispatch({ type: 'SET_LAST_VISIBLE', payload: snap.docs[snap.docs.length - 1] });
    dispatch({ type: 'SET_HAS_MORE', payload: snap.docs.length === (opts.startAfterDoc ? PAGE_SIZE : 30) });
    dispatch({ type: 'SET_LOADING_MORE', payload: false });
  };

  // ⚡️ PERFORMANCE OPTIMIZATION: Combined Firestore and socket room management
  useEffect(() => {
    if (!user || !conversation) return;
    
    let convId = null;
    if (firestoreConvId) {
      convId = firestoreConvId;
    } else if (conversation.id === 'admin') {
      convId = `agent_${user.uid}`;
    }
    if (!convId) return;

    dispatch({ type: 'SET_LOCAL_FIRESTORE_CONV_ID', payload: convId });
    
    // Load initial messages without loading state
    loadMessages(convId);
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Join socket room immediately after conversation setup
    if (isConnected && conversation && conversation.id) {
      joinRoom(conversation.id);
    }
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Clear message deduplication cache when switching conversations
    seenMessageIds.current.clear();
    
  }, [user, conversation, firestoreConvId, isConnected, joinRoom, loadMessages]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced scroll handler for lazy loading
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    const handleScrollEvent = () => {
      // Handle lazy loading of older messages
      handleScroll(container);
      
      // ⚡️ OPTIMIZATION: Load older messages when user scrolls to top
      if (container.scrollTop === 0 && hasMore && !loadingMore) {
        // For agent conversations
        if (firestoreConvId || localFirestoreConvId) {
        loadMessages(firestoreConvId || localFirestoreConvId, { startAfterDoc: lastVisible, append: true });
        }
        // For regular conversations - implement similar logic if needed
        // This would require tracking the oldest message for regular conversations
      }
      
      // Also handle position tracking for auto-scroll
      handleScrollPosition();
    };
    
    container.addEventListener('scroll', handleScrollEvent);
    return () => container.removeEventListener('scroll', handleScrollEvent);
  }, [hasMore, loadingMore, firestoreConvId, localFirestoreConvId, lastVisible, handleScroll]);

  useEffect(() => {
    if (!showEmojiPicker) return;
    function handleClickOutside(event) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        dispatch({ type: 'TOGGLE_EMOJI_PICKER' });
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  // 🎨 WHATSAPP-STYLE SMOOTH SCROLLING: Smart auto-scroll that preserves user position
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const lastMessageCountRef = useRef(0);

  // Check if user is near bottom (within 100px)
  const checkIfNearBottom = useCallback(() => {
    if (!chatContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const threshold = 100;
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, []);

  // Smooth scroll to bottom with proper behavior
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (!chatContainerRef.current) return;
    
    const container = chatContainerRef.current;
    const targetScrollTop = container.scrollHeight - container.clientHeight;
    
    if (behavior === 'instant') {
      container.scrollTop = targetScrollTop;
    } else {
      container.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      });
    }
  }, []);

  // Handle scroll events to track user position
  const handleScrollPosition = useCallback(() => {
    if (!chatContainerRef.current) return;
    
    const nearBottom = checkIfNearBottom();
    setIsNearBottom(nearBottom);
    
    // If user scrolls up, disable auto-scroll
    if (!nearBottom && shouldAutoScroll) {
      setShouldAutoScroll(false);
    }
    // If user scrolls back to bottom, re-enable auto-scroll
    else if (nearBottom && !shouldAutoScroll) {
      setShouldAutoScroll(true);
    }
  }, [checkIfNearBottom, shouldAutoScroll]);



  // Smart auto-scroll logic
  useEffect(() => {
    if (!chatContainerRef.current) return;

    const currentMessageCount = allVisibleMessages.length;
    const isNewMessage = currentMessageCount > lastMessageCountRef.current;
    const isFirstLoad = lastMessageCountRef.current === 0;

    // Always scroll on first load
    if (isFirstLoad) {
      scrollToBottom('instant');
      setShouldAutoScroll(true);
      setIsNearBottom(true);
    }
    // Auto-scroll for new messages only if user is near bottom or should auto-scroll
    else if (isNewMessage && (shouldAutoScroll || isNearBottom)) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        scrollToBottom('smooth');
      }, 50);
    }

    lastMessageCountRef.current = currentMessageCount;
  }, [allVisibleMessages.length, shouldAutoScroll, isNearBottom, scrollToBottom]);

  // Reset auto-scroll when conversation changes
  useEffect(() => {
    setShouldAutoScroll(true);
    setIsNearBottom(true);
    lastMessageCountRef.current = 0;
  }, [conversation?.id]);

  // 🎨 REPLY FUNCTIONALITY: Handle reply to message
  const handleReplyToMessage = useCallback((message) => {
    setReplyToMessage(message);
    // Focus the input after setting reply
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  }, []);

  // 🎨 REPLY FUNCTIONALITY: Cancel reply
  const handleCancelReply = useCallback(() => {
    setReplyToMessage(null);
  }, []);

  // 🎨 REPLY FUNCTIONALITY: Clear reply when conversation changes
  useEffect(() => {
    setReplyToMessage(null);
  }, [conversation?.id]);

  // 🎨 CONTEXT MENU: Handle context menu actions
  const handleContextMenuAction = useCallback((action, message) => {
    switch (action) {
      case 'reply':
        handleReplyToMessage(message);
        break;
      case 'forward':
        // TODO: Implement forward functionality
        console.log('Forward message:', message);
        break;
      case 'star':
        // TODO: Implement star functionality
        console.log('Star message:', message);
        break;
      case 'pin':
        // TODO: Implement pin functionality
        console.log('Pin message:', message);
        break;
      case 'favorite':
        // TODO: Implement favorite functionality
        console.log('Add to favorites:', message);
        break;
      case 'delete':
        // TODO: Implement delete functionality
        console.log('Delete message:', message);
        break;
      case 'select':
        // TODO: Implement select functionality
        console.log('Select message:', message);
        break;
      case 'share':
        // TODO: Implement share functionality
        console.log('Share message:', message);
        break;
      case 'react':
        // TODO: Implement reaction functionality
        console.log('React to message:', message);
        break;
      default:
        console.log('Unknown action:', action, message);
    }
  }, [handleReplyToMessage]);

  // 🎨 CONTEXT MENU: Show context menu
  const showContextMenu = useCallback((e, message) => {
    e.preventDefault();
    setContextMenu({
      isVisible: true,
      position: { x: e.clientX, y: e.clientY },
      message: message
    });
  }, []);

  // 🎨 CONTEXT MENU: Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu({
      isVisible: false,
      position: { x: 0, y: 0 },
      message: null
    });
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced Firestore listener with proper cleanup
  useEffect(() => {
    let unsubscribe = null;
    let convId = null;
    
    if (!user || !conversation) return;
    if (firestoreConvId) {
      convId = firestoreConvId;
    } else if (conversation.id === 'admin') {
      convId = `agent_${user.uid}`;
    }
    if (!convId) return;

    const messagesCol = collection(db, 'agentConversations', convId, 'messages');
    // ⚡️ PERFORMANCE OPTIMIZATION: Load messages in ascending order for real-time updates
    const q = query(messagesCol, orderBy('timestamp', 'asc'));
    
    unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          time: data.timestamp ? new Date(data.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
      });
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Messages are already in correct order (ascending)
      // Remove pending messages that have been confirmed by Firestore
      dispatch({ 
        type: 'UPDATE_PENDING_MESSAGES', 
        payload: pendingMessages.filter(pending => {
          const isConfirmed = msgs.some(firestore => 
              firestore.tempId && firestore.tempId === pending.id
          );
          return !isConfirmed;
        })
      });
      
      dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: msgs });
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Cache messages in IndexedDB for offline-first experience
      try {
        offlineStorage.cacheMessages(convId, msgs);
      } catch (error) {
        console.warn('Failed to cache messages in IndexedDB:', error);
      }
    });
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Proper cleanup to prevent memory leaks
    return () => {
      if (unsubscribe) {
        unsubscribe();
        console.log(`Cleaned up Firestore listener for conversation ${convId}`);
      }
    };
  }, [user, conversation, firestoreConvId]);

  // Focus the input after sending is done
  useEffect(() => {
    if (!sending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [sending]);

  // Update agent chat preview in sidebar for both sent and received messages
  useEffect(() => {
    if (
      conversation &&
      conversation.id === 'admin' &&
      firestoreMessages &&
      firestoreMessages.length > 0 &&
      typeof updateAgentChatPreview === 'function'
    ) {
      const lastMsg = firestoreMessages[firestoreMessages.length - 1];
      if (lastMsg && lastMsg.text) {
        updateAgentChatPreview(lastMsg.text);
      }
    }
  }, [firestoreMessages, conversation, updateAgentChatPreview]);

  // Firestore: Real-time listener for user-to-user chat rooms (buyer-seller)
  useEffect(() => {
    if (!conversation || !user) return;
    if (conversation.buyer && conversation.seller) {
      dispatch({ type: 'SET_FIRESTORE_LOADING', payload: true });
      const messagesCol = collection(db, 'sellerbuyerconversations', conversation.id, 'messages');
      // ⚡️ PERFORMANCE OPTIMIZATION: Load messages in ascending order for real-time updates
      const q = query(messagesCol, orderBy('timestamp', 'asc'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const msgs = snapshot.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          // If this is an order message, check if the status has been updated
          if (data.type === 'order' && data.order_id) {
            // Get the latest status from the conversation document
            const convRef = doc(db, 'sellerbuyerconversations', conversation.id);
            getDoc(convRef).then(convDoc => {
              if (convDoc.exists()) {
                const convData = convDoc.data();
                if (convData.order_id === data.order_id && convData.status !== data.status) {
                  // Update the message with the new status
                  const messageRef = doc(db, 'sellerbuyerconversations', conversation.id, 'messages', docSnapshot.id);
                  updateDoc(messageRef, {
                    status: convData.status
                  });
                }
              }
            });
          }
          return {
            id: docSnapshot.id,
            ...data,
            time: data.timestamp ? new Date(data.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };
        });
        
        // ⚡️ PERFORMANCE OPTIMIZATION: Messages are already in correct order (ascending)
        // Remove pending messages that have been confirmed by Firestore
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGES', 
          payload: pendingMessages.filter(pending => {
            const isConfirmed = msgs.some(firestore => 
              firestore.tempId && firestore.tempId === pending.id
            );
            return !isConfirmed;
          })
        });
        
        dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: msgs });
        dispatch({ type: 'SET_FIRESTORE_LOADING', payload: false });
        
        // ⚡️ PERFORMANCE OPTIMIZATION: Cache messages in IndexedDB for offline-first experience
        try {
          offlineStorage.cacheMessages(conversation.id, msgs);
        } catch (error) {
          console.warn('Failed to cache messages in IndexedDB:', error);
        }
      });
      return () => unsubscribe();
    }
  }, [conversation, user]);

  // Always use Firestore messages for buyer-seller conversations
  const isUserAgentChat = conversation && (conversation.id === 'admin' && !isAdminDashboard || !!(firestoreConvId || localFirestoreConvId));
  const isUserToUserChat = conversation && conversation.buyer && conversation.seller;
  const chatMessages = isUserAgentChat ? firestoreMessages : (isUserToUserChat ? firestoreMessages : []);

  // Now safe to use chatMessages below
  // Helper to check if the uploading file's URL is in chatMessages
  const fileMessageInChat = uploadedFileUrl && chatMessages.some(msg => msg && msg.url === uploadedFileUrl);

  // Only hide preview when the message is visible in chat
  useEffect(() => {
    if (
      uploading &&
      uploadingFile &&
      uploadedFileUrl &&
      chatMessages.some(msg => msg && msg.url === uploadedFileUrl)
    ) {
        dispatch({ type: 'SET_UPLOADING', payload: false });
        dispatch({ type: 'SET_UPLOADING_FILE', payload: null });
        dispatch({ type: 'SET_UPLOADED_FILE_URL', payload: null });
    }
  }, [chatMessages, uploading, uploadingFile, uploadedFileUrl]);



  // Send file message
  const handleSendFileMessage = async (url, type) => {
    if (!user || !conversation) return;
    if (!url || !(type && (type.startsWith('image/') || type.startsWith('video/')))) return; // Prevent empty file messages
    
    // ⚡️ INSTANT UI UPDATE: Create optimistic file message for immediate display
    const optimisticFileMessage = {
      id: `temp_file_${Date.now()}_${Math.random()}`,
      from: user.email,
      type: type && type.startsWith('image/') ? 'image' : 'video',
      url,
      time: new Date().toLocaleTimeString(),
      status: 'sending',
      timestamp: new Date()
    };

    // ⚡️ INSTANT UI UPDATE: Add to pending messages immediately (no delay)
    dispatch({ type: 'ADD_PENDING_MESSAGE', payload: optimisticFileMessage });
    
    let messagesCol;
    if (conversation.id === 'admin' || isAdminDashboard) {
      const convId = firestoreConvId || (conversation.id === 'admin' ? `agent_${user.uid}` : conversation.id);
      if (!convId) return;
      messagesCol = collection(db, 'agentConversations', convId, 'messages');
    } else if (conversation.buyer && conversation.seller) {
      messagesCol = collection(db, 'sellerbuyerconversations', conversation.id, 'messages');
    } else {
      return;
    }
    
    // Security: Validate user data before saving file message
    if (!user || !user.email) {
      console.error('User data is missing for file message save');
      if (typeof toast !== 'undefined') {
        toast.error('Authentication error. Please refresh and try again.');
      }
      return;
    }
    
    const docRef = await addDoc(messagesCol, {
      from: user.email,
      fromUid: user.uid || user.email, // Fallback to email if uid is undefined
      type: type && type.startsWith('image/') ? 'image' : 'video',
      url,
      timestamp: serverTimestamp(),
      readBy: [user.email],
    });
    
    // Update optimistic message status to 'sent'
    dispatch({ 
      type: 'UPDATE_PENDING_MESSAGES', 
      payload: pendingMessages.map(msg => 
        msg.url === url && msg.status === 'sending' 
          ? { ...msg, status: 'sent', firestoreId: docRef.id }
          : msg
      )
    });
  };

  // Determine the other party's name, avatar, and online status for the header
  let otherPartyName = '';
  let otherPartyAvatar = '';
  
  // Debug: Log conversation data to understand the structure
  if (process.env.NODE_ENV === 'development' && false) {
    console.log('ChatPanel Debug - Conversation Data:', {
      conversation,
      user: user?.email,
      isAdminDashboard,
      buyer: conversation?.buyer,
      seller: conversation?.seller
    });
  }
  
  if (isAdminDashboard && conversation && (conversation.userEmail || conversation.userName)) {
    otherPartyName = conversation.userName || conversation.userEmail || 'Unknown User';
    otherPartyAvatar = conversation.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherPartyName)}&background=dddddd&color=222222&rounded=true&size=64`;
  } else if (user && conversation && conversation.buyer && conversation.seller) {
    // Check if current user is the buyer
    if (conversation.buyer.email && conversation.buyer.email.toLowerCase() === user.email.toLowerCase()) {
      // Current user is buyer, show seller info
      otherPartyName = conversation.seller.name || conversation.seller_name || conversation.seller.username || 'Seller';
      otherPartyAvatar = conversation.seller.avatar || conversation.seller_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherPartyName)}&background=dddddd&color=222222&rounded=true&size=64`;
    } else if (conversation.seller.email && conversation.seller.email.toLowerCase() === user.email.toLowerCase()) {
      // Current user is seller, show buyer info
      otherPartyName = conversation.buyer.name || conversation.buyer_name || conversation.buyer.username || 'Buyer';
      otherPartyAvatar = conversation.buyer.avatar || conversation.buyer_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherPartyName)}&background=dddddd&color=222222&rounded=true&size=64`;
    } else {
      // Fallback: try to determine based on UID
      if (conversation.buyer.uid && conversation.buyer.uid === user.uid) {
        otherPartyName = conversation.seller.name || conversation.seller_name || conversation.seller.username || 'Seller';
        otherPartyAvatar = conversation.seller.avatar || conversation.seller_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherPartyName)}&background=dddddd&color=222222&rounded=true&size=64`;
      } else if (conversation.seller.uid && conversation.seller.uid === user.uid) {
        otherPartyName = conversation.buyer.name || conversation.buyer_name || conversation.buyer.username || 'Buyer';
        otherPartyAvatar = conversation.buyer.avatar || conversation.buyer_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherPartyName)}&background=dddddd&color=222222&rounded=true&size=64`;
      }
    }
  }

  // Add debounce to handleSend and handleSendAgentMessage:
  const lastSentRef = useRef(0);
  const MIN_SEND_INTERVAL = 1000; // 1 second

  // ⚡️ WHATSAPP-STYLE BACKGROUND PROCESSING: Handle server communication with proper sync
  const processMessageInBackground = async (instantMessage) => {
    try {
      const messageText = instantMessage.text;
      
      // Security: Enhanced rate limiting check
      const rateLimit = checkRateLimit(user.uid);
      if (!rateLimit.allowed) {
        // Update message status to failed
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGE', 
          payload: { 
            id: instantMessage.id, 
            status: 'failed',
            error: `Rate limit exceeded. Please wait ${Math.ceil(rateLimit.remainingTime / 1000)} seconds.`
          } 
        });
        return;
      }
      
      // Security: Input validation
      const inputValidation = validateInput(messageText);
      if (!inputValidation.isValid) {
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGE', 
          payload: { 
            id: instantMessage.id, 
            status: 'failed',
            error: inputValidation.error
          } 
        });
        return;
      }
      
      // Security: Enhanced sanitization
      const sanitizedText = sanitizeText(messageText);
      
      // Security: Log message sending
      logSecurityEvent('MESSAGE_SENT', { 
        conversationId: conversation?.id, 
        messageLength: sanitizedText.length,
        isAgent: true 
      });
      
      let convId = firestoreConvId || localFirestoreConvId;
      if (!convId && !isAdminDashboard) {
        convId = `agent_${user.uid}`;
      }
      if (!convId) {
        dispatch({ type: 'SET_SENDING', payload: false });
        return;
      }
      
      // Security: Send sanitized message via socket with CSRF protection
      if (user && isConnected && conversation && conversation.id) {
        sendMessage(sanitizedText, convId, csrfToken);
      }
      
      // Security: Save sanitized message to Firestore
      const messagesCol = collection(db, 'agentConversations', convId, 'messages');
      
      const messageData = {
        text: sanitizedText,
        from: user.email,
        fromUid: user.uid,
        timestamp: serverTimestamp(),
        status: 'sent',
        readBy: [user.email],
        readByAdmin: isAdminDashboard,
        isAgent: true,
        conversationId: convId,
        tempId: instantMessage.id // ⚡️ SERVER SYNC: Link temp ID to real message
      };
      
      const docRef = await addDoc(messagesCol, messageData);
      
      // ⚡️ SERVER ACKNOWLEDGMENT: Replace pending message with confirmed message
      const confirmedMessage = {
        ...messageData,
        id: docRef.id,
        timestamp: new Date(),
        time: new Date().toLocaleTimeString(),
        status: 'sent',
        tempId: instantMessage.id, // ⚡️ SERVER SYNC: Keep temp ID for sync
        isConfirmed: true // ⚡️ SERVER SYNC: Mark as server-confirmed
      };
      
      dispatch({ 
        type: 'REPLACE_PENDING_MESSAGE', 
        payload: { 
          tempId: instantMessage.id,
          realMessage: confirmedMessage
        } 
      });
      
      // Update conversation preview
      if (updateAgentChatPreview) {
        updateAgentChatPreview(convId, sanitizedText);
      }
      
      // Move conversation to top
      if (moveConversationToTop) {
        moveConversationToTop(convId);
      }
      
    } catch (error) {
      console.error('Background message processing failed:', error);
      
      // ⚡️ ERROR HANDLING: Update message status to failed
      dispatch({ 
        type: 'UPDATE_PENDING_MESSAGE', 
        payload: { 
          id: instantMessage.id, 
          status: 'failed',
          error: 'Failed to send message. Please try again.'
        } 
      });
    } finally {
      dispatch({ type: 'SET_SENDING', payload: false });
    }
  };

  const handleSendAgentMessage = async (e, instantMessage = null) => {
    e.preventDefault();
    if (!user) return;
    
    // ⚡️ WHATSAPP-STYLE INSTANT DISPLAY: If instant message provided, display immediately
    if (instantMessage && instantMessage.isOptimistic) {
      // ⚡️ INSTANT UI UPDATE: Add message to chat immediately (0ms delay)
      dispatch({ type: 'ADD_PENDING_MESSAGE', payload: instantMessage });
      dispatch({ type: 'SET_SENDING', payload: true });
      
      // ⚡️ BACKGROUND PROCESSING: Start server communication in background
      setTimeout(() => {
        processMessageInBackground(instantMessage);
      }, 0);
      
      return; // Exit early to prevent double processing
    }
    
    // Security: Enhanced rate limiting check with visual feedback
    const rateLimit = checkRateLimit(user.uid);
    if (!rateLimit.allowed) {
      const remainingSeconds = Math.ceil(rateLimit.remainingTime / 1000);
      
      // Security: Log rate limit violation
      logSecurityEvent('RATE_LIMIT_VIOLATION', {
        userId: user.uid,
        remainingTime: remainingSeconds,
        limit: MAX_MESSAGES_PER_MINUTE
      }, 'warning');
      
      if (typeof toast !== 'undefined') {
        toast.error(`Rate limit exceeded. Please wait ${remainingSeconds} seconds.`);
      } else {
        alert(`Rate limit exceeded. Please wait ${remainingSeconds} seconds.`);
      }
      return;
    }
    
    // Security: Show remaining messages count
    if (rateLimit.remainingMessages <= 3) {
      if (typeof toast !== 'undefined') {
        toast.warning(`You have ${rateLimit.remainingMessages} messages remaining this minute.`);
      }
    }
    
    // Security: Input validation
    const inputValidation = validateInput(input);
    if (!inputValidation.isValid) {
      if (typeof toast !== 'undefined') {
        toast.error(inputValidation.error);
      } else {
        alert(inputValidation.error);
      }
      return;
    }
    
    const messageText = input.trim();
    
    // ⚡️ INSTANT UI UPDATE: Clear input immediately (before any processing)
    dispatch({ type: 'SET_INPUT', payload: '' });
    
    // ⚡️ STEP 1: Create temporary message with unique ID for instant display
    const tempId = instantMessage ? instantMessage.id : `temp_${Date.now()}_${Math.random()}`;
    const tempMessage = instantMessage || {
      id: tempId,
      text: messageText, // Use original text for instant display
      from: user.email,
      fromUid: user.uid,
      time: new Date().toLocaleTimeString(),
      status: 'sending',
      timestamp: new Date(),
      isPending: true
    };

    // ⚡️ STEP 1: Show message instantly in chat panel (like WhatsApp)
    if (!instantMessage) {
      dispatch({ type: 'ADD_PENDING_MESSAGE', payload: tempMessage });
      dispatch({ type: 'SET_SENDING', payload: true });
    }
    
    // Security: Enhanced sanitization (after instant display)
    const sanitizedText = sanitizeText(messageText);
    
    // Security: Log message sending
    logSecurityEvent('MESSAGE_SENT', { 
      conversationId: conversation?.id, 
      messageLength: sanitizedText.length,
      isAgent: true 
    });
    let convId = firestoreConvId || localFirestoreConvId;
    if (!convId && !isAdminDashboard) {
      convId = `agent_${user.uid}`;
    }
    if (!convId) {
      dispatch({ type: 'SET_SENDING', payload: false });
      return;
    }
    
    // Security: Send sanitized message via socket with CSRF protection
    if (user && isConnected && conversation && conversation.id) {
      sendMessage(sanitizedText, convId, csrfToken);
    }
    
    // Security: Save sanitized message to Firestore
    const messagesCol = collection(db, 'agentConversations', convId, 'messages');
    
    // Security: Validate user data before saving
    if (!user || !user.email) {
      console.error('User data is missing for agent message save');
      if (typeof toast !== 'undefined') {
        toast.error('Authentication error. Please refresh and try again.');
      }
        dispatch({ type: 'SET_SENDING', payload: false });
      return;
    }
    
    // ⚡️ STEP 2: Upload real message to Firestore
    try {
    const docRef = await addDoc(messagesCol, {
      from: user.email,
      fromUid: user.uid || user.email, // Fallback to email if uid is undefined
      type: 'text',
      text: sanitizedText, // ✅ SANITIZED
      timestamp: serverTimestamp(),
      readBy: [user.email],
    });
    
    // Update the conversation document
    await updateDoc(doc(db, 'agentConversations', convId), {
      lastMessage: messageText,
      lastMessageTime: serverTimestamp(),
    });
    
    // ⚡️ STEP 2: Replace temp message with real one (like WhatsApp)
    const realMessage = {
      id: docRef.id,
      text: sanitizedText,
      from: user.email,
      fromUid: user.uid,
      time: new Date().toLocaleTimeString(),
      status: 'sent',
      timestamp: new Date(),
      firestoreId: docRef.id
    };
    
    // Replace the temp message with the real one
    dispatch({ 
      type: 'REPLACE_PENDING_MESSAGE', 
      payload: { tempId, realMessage }
    });
    
    dispatch({ type: 'SET_SENDING', payload: false });
    dispatch({ type: 'SET_SHOW_TICK', payload: true });
    setTimeout(() => dispatch({ type: 'SET_SHOW_TICK', payload: false }), 1000);
    
      } catch (error) {
      // ⚡️ STEP 3: Handle upload failure (like WhatsApp)
      console.error('Failed to send message:', error);
      
      // Update temp message to show failed status
      dispatch({ 
        type: 'REPLACE_PENDING_MESSAGE', 
        payload: { 
          tempId, 
          realMessage: {
            id: tempId,
            text: sanitizedText,
            from: user.email,
            fromUid: user.uid,
            time: new Date().toLocaleTimeString(),
            status: 'failed',
            timestamp: new Date(),
            error: error.message
          }
        }
      });
      
      dispatch({ type: 'SET_SENDING', payload: false });
      
      // Show error to user
      if (typeof toast !== 'undefined') {
        toast.error('Failed to send message. Please try again.');
      } else {
        alert('Failed to send message. Please try again.');
      }
    }
  };

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize send message handler
  const handleSendMessage = useCallback(async (messageText, instantMessage = null) => {
    if (!conversation || !user) return;
    
    // ⚡️ INSTANT DISPLAY: If instant message provided, display immediately
    if (instantMessage) {
      dispatch({ type: 'ADD_PENDING_MESSAGE', payload: instantMessage });
      dispatch({ type: 'SET_SENDING', payload: true });
    }
    
    // Security: Enhanced sanitization
    const sanitizedText = sanitizeText(messageText);
    
    // Security: Log message sending
    logSecurityEvent('MESSAGE_SENT', { 
      conversationId: conversation.id, 
      messageLength: sanitizedText.length,
      isAgent: false 
    });
    
      // Enhanced offline handling with local caching
  if (!isOnline) {
    const offlineMessage = {
      from: user.email,
      fromUid: user.uid, // Security: Store UID for server-side validation
      text: sanitizedText,
      timestamp: new Date(),
      type: 'offline',
      conversationId: conversation.id,
      // Add integrity hash for offline messages too
      integrityHash: CryptoJS.SHA256(
        `${sanitizedText}${user.email}${Date.now()}`
      ).toString()
    };
    
    // Add to offline queue for sync when back online
    addToOfflineQueue(offlineMessage);
    
    // Add message to UI immediately with offline indicator
    const tempMessage = {
      id: `offline_${Date.now()}`,
      from: user.email,
      text: sanitizedText, // ✅ SANITIZED
      time: new Date().toLocaleTimeString(),
      status: 'offline',
      isOffline: true
    };
    dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [...firestoreMessages, tempMessage] });
    
    // Security: Log offline message creation
    logSecurityEvent('OFFLINE_MESSAGE_CREATED', {
      conversationId: conversation.id,
      messageLength: sanitizedText.length
    }, 'info');
    
    return;
  }

    // Create optimistic message (appears instantly)
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const optimisticMessage = {
      id: tempId,
      from: user.email,
      fromUid: user.uid, // Security: Store UID for server-side validation
      text: sanitizedText,
      time: new Date().toLocaleTimeString(),
      status: 'sending', // ⏳ Show sending status
      timestamp: new Date(),           // keep for UI
      clientCreatedAt: Date.now(),     // ← use in sort fallback
      isPending: true,
      isOptimistic: true,              // ← important
      replyTo: replyToMessage ? {      // 🎨 REPLY: Include reply information (sanitized)
        id: replyToMessage.id || null,
        text: replyToMessage.text || null,
        from: replyToMessage.from || null,
        name: replyToMessage.name || null,
        type: replyToMessage.type || 'text'
      } : null
    };

    // Add to pending messages immediately (no Firestore reload)
    dispatch({ type: 'ADD_PENDING_MESSAGE', payload: optimisticMessage });
    
    // 🎨 REPLY: Clear reply immediately (no delay)
    setReplyToMessage(null);
    
    dispatch({ type: 'SET_SENDING', payload: true });
    if (conversation.id === 'admin' || (isAdminDashboard && activeTab === 'agent')) {
      await handleSendAgentMessage({ preventDefault: () => {} });
      dispatch({ type: 'SET_SENDING', payload: false });
      dispatch({ type: 'SET_SHOW_TICK', payload: true });
      setTimeout(() => dispatch({ type: 'SET_SHOW_TICK', payload: false }), 1000);
      return;
    }
    const senderId = user?.uid || user?.email || 'unknown';
    if (conversation.buyer && conversation.seller) {
      try {
      // Security: Send sanitized message via socket with CSRF protection
      if (user && isConnected && conversation && conversation.id) {
        sendMessage(sanitizedText, conversation.id, csrfToken);
      }
      // Security: Save sanitized message to Firestore
      const messagesCol = collection(db, 'sellerbuyerconversations', conversation.id, 'messages');
      
      // Security: Validate user data before saving
      if (!user || !user.email) {
        console.error('User data is missing for message save');
        if (typeof toast !== 'undefined') {
          toast.error('Authentication error. Please refresh and try again.');
        }
        dispatch({ type: 'SET_SENDING', payload: false });
        return;
      }
      
      const docRef = await addDoc(messagesCol, {
        from: user.email,
        fromUid: user.uid || user.email, // Fallback to email if uid is undefined
        type: 'text',
        text: sanitizedText, // ✅ SANITIZED
        timestamp: serverTimestamp(),
        clientCreatedAt: optimisticMessage.clientCreatedAt, // optional but helpful
        tempId,                                       // ← link to pending
        readBy: [user.email],
        replyTo: replyToMessage ? {                    // 🎨 REPLY: Include reply information in Firestore
          id: replyToMessage.id || null,
          text: replyToMessage.text || null,
          from: replyToMessage.from || null,
          name: replyToMessage.name || null,
          type: replyToMessage.type || 'text'
        } : null
      });
      
      // Update the conversation document
      await updateDoc(doc(db, 'sellerbuyerconversations', conversation.id), {
        lastMessage: sanitizedText,
        lastMessageTime: serverTimestamp(),
      });
      
      // Update optimistic message status to 'sent' (no reload needed)
      dispatch({ 
        type: 'UPDATE_PENDING_MESSAGES', 
        payload: pendingMessages.map(msg => 
          msg.id === tempId && msg.status === 'sending' 
            ? { ...msg, status: 'sent', firestoreId: docRef.id }
            : msg
        )
      });
      
      dispatch({ type: 'SET_SENDING', payload: false });
      dispatch({ type: 'SET_SHOW_TICK', payload: true });
      setTimeout(() => dispatch({ type: 'SET_SHOW_TICK', payload: false }), 1000);
      return;
      } catch (error) {
        console.error('Failed to send message:', error);
        
        // Update optimistic message to show failed status
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGES', 
          payload: pendingMessages.map(msg => 
            msg.id === tempId && msg.status === 'sending' 
              ? { ...msg, status: 'failed', error: error.message }
              : msg
          )
        });
        
        dispatch({ type: 'SET_SENDING', payload: false });
        
        if (typeof toast !== 'undefined') {
          toast.error('Failed to send message. Please try again.');
        } else {
          alert('Failed to send message. Please try again.');
        }
        return;
      }
    }
    if (activeTab !== 'transactions') {
      dispatch({ type: 'SET_SENDING', payload: false });
      return;
    }
    const newMsg = {
      id: currentMessages.length + 1,
      from: senderId,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      text: sanitizedText,
    };
    dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [...firestoreMessages, newMsg] });
    if (moveConversationToTop) {
      moveConversationToTop(conversation.id, sanitizedText);
    }
    if (updateMessagesPreview) updateMessagesPreview(conversation.id, sanitizedText);
    dispatch({ type: 'SET_SENDING', payload: false });
    dispatch({ type: 'SET_SHOW_TICK', payload: true });
    setTimeout(() => dispatch({ type: 'SET_SHOW_TICK', payload: false }), 1000);
  }, [conversation, user, isOnline, addToOfflineQueue, dispatch, isAdminDashboard, activeTab, handleSendAgentMessage, isConnected, sendMessage, csrfToken, currentMessages, moveConversationToTop, updateMessagesPreview, replyToMessage]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced file upload with background processing
  const handleFileUpload = useCallback(async (file) => {
    if (!file || !user) return;
    
    // Security: File upload rate limiting with server-side tracking
    const fileRateLimit = await checkFileUploadRateLimit(user.uid);
    if (!fileRateLimit.allowed) {
      const remainingSeconds = Math.ceil(fileRateLimit.remainingTime / 1000);
      if (typeof toast !== 'undefined') {
        toast.error(`File upload rate limit exceeded. Please wait ${remainingSeconds} seconds.`);
      } else {
        alert(`File upload rate limit exceeded. Please wait ${remainingSeconds} seconds.`);
      }
      return;
    }
    
    // Security: Log file upload attempt
    logSecurityEvent('FILE_UPLOAD_ATTEMPT', { 
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId: user.uid
    }, 'info');
    
    // Security: Validate file using server-side rules
    const validation = validateFile(file);
    if (!validation.isValid) {
      logSecurityEvent('FILE_UPLOAD_REJECTED', { 
        fileName: file.name,
        reason: validation.error 
      }, 'warning');
      
      if (typeof toast !== 'undefined') {
        toast.error(validation.error);
      } else {
        alert(validation.error);
      }
      return;
    }

    // ⚡️ PERFORMANCE OPTIMIZATION: Show upload preview immediately
    dispatch({ type: 'SET_UPLOADING_FILE', payload: {
      file,
      type: file.type,
      name: file.name,
      size: file.size,
      previewUrl: URL.createObjectURL(file)
    }});
    dispatch({ type: 'SET_UPLOADING', payload: true });
    dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });

    // ⚡️ PERFORMANCE OPTIMIZATION: Background upload processing
    try {
      const fileRef = storageRef(storage, `chat_files/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(fileRef, file);
      
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          // ⚡️ PERFORMANCE OPTIMIZATION: Use requestIdleCallback for non-critical updates
          if (window.requestIdleCallback) {
            requestIdleCallback(() => {
              dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: progress });
            });
          } else {
            dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: progress });
          }
        },
        (error) => {
          console.error('Upload failed:', error);
          dispatch({ type: 'SET_UPLOADING_FILE', payload: null });
          dispatch({ type: 'SET_UPLOADING', payload: false });
          dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });
          if (typeof toast !== 'undefined') {
            toast.error('File upload failed.');
          } else {
            alert('File upload failed.');
          }
        },
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          
          // ⚡️ PERFORMANCE OPTIMIZATION: Cache the media file
          const mediaId = `${Date.now()}_${file.name}`;
          chatCache.cacheMedia(mediaId, {
            url,
            type: file.type,
            name: file.name,
            size: file.size
          });
          
          // ⚡️ PERFORMANCE OPTIMIZATION: Send message after upload completes
          await handleSendFileMessage(url, file.type);
          dispatch({ type: 'SET_UPLOADING_FILE', payload: null });
          dispatch({ type: 'SET_UPLOADING', payload: false });
          dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });
        }
      );
    } catch (error) {
      console.error('Upload failed:', error);
      dispatch({ type: 'SET_UPLOADING_FILE', payload: null });
      dispatch({ type: 'SET_UPLOADING', payload: false });
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });
      if (typeof toast !== 'undefined') {
        toast.error('Upload failed. Please try again.');
      } else {
        alert('Upload failed. Please try again.');
      }
    }
  }, [user, dispatch, chatCache, handleSendFileMessage]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize image click handler
  const handleImageClick = useCallback((imageUrl) => {
    dispatch({ type: 'SET_OPENED_IMAGE', payload: imageUrl });
  }, [dispatch]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced debounced typing indicator with throttling
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const lastTypingEmitRef = useRef(0);
  const typingQueueRef = useRef([]);

  const debouncedTypingIndicator = useCallback((isUserTyping) => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    setIsTyping(isUserTyping);
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced throttling with queue management
    if (isUserTyping) {
      const now = Date.now();
      if (now - lastTypingEmitRef.current > 500) { // Only emit every 500ms
        lastTypingEmitRef.current = now;
        
        // Add to queue for batch processing
        typingQueueRef.current.push({ isTyping: true, conversationId: conversation?.id });
        
        // Emit typing event to socket
        if (isConnected) {
          sendMessage('typing', { isTyping: true, conversationId: conversation?.id });
        }
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        
        // Add stop typing to queue
        typingQueueRef.current.push({ isTyping: false, conversationId: conversation?.id });
        
        // Emit stop typing event
        if (isConnected) {
          sendMessage('typing', { isTyping: false, conversationId: conversation?.id });
        }
        
        // Clear queue after processing
        typingQueueRef.current = [];
      }, 1000); // Stop typing indicator after 1 second of inactivity
    }
  }, [isConnected, sendMessage, conversation?.id]);

  const handleEmojiClick = (emoji) => {
    // Security: Validate emoji before adding
    if (!validateEmoji(emoji)) {
      logSecurityEvent('INVALID_EMOJI_ATTEMPT', { emoji }, 'warning');
      if (typeof toast !== 'undefined') {
        toast.error('Invalid emoji detected');
      }
      return;
    }
    
    dispatch({ type: 'SET_INPUT', payload: input + emoji });
    dispatch({ type: 'TOGGLE_EMOJI_PICKER' });
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Trigger typing indicator
    debouncedTypingIndicator(true);
  };

  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    dispatch({ type: 'SET_DRAG_OVER', payload: true });
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dispatch({ type: 'SET_DRAG_OVER', payload: false });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    dispatch({ type: 'SET_DRAG_OVER', payload: false });
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Handle the first file (you can extend this to handle multiple files)
      const file = files[0];
      await handleFileUpload(file);
    }
  };



  const canSendMessage = true;

  // ⚡️ PERFORMANCE OPTIMIZATION: Socket message deduplication
  const deduplicateMessage = useCallback((message) => {
    const messageId = message.id || `${message.from}_${message.timestamp}_${message.text}`;
    
    // Check if we've already seen this message
    if (seenMessageIds.current.has(messageId)) {
      console.log(`Duplicate message detected: ${messageId}`);
      return false;
    }
    
    // Add to seen messages
    seenMessageIds.current.add(messageId);
    
    // Clean up old message IDs after 5 minutes to prevent memory leaks
    setTimeout(() => {
      seenMessageIds.current.delete(messageId);
    }, 5 * 60 * 1000);
    
    return true;
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Extract message processing logic
  const processMessages = useCallback((messages, isAdmin, conv, userEmail) => {
    const filteredMessages = messages.filter(msg =>
    msg && (
      (msg.text && msg.text.trim() !== '') ||
      (msg.type === 'image' && msg.url) ||
      (msg.type === 'video' && msg.url) ||
      (msg.type === 'order' && (
        // Show order messages for admin dashboard
          isAdmin ||
        // Show order messages for seller if they are the seller
          (conv && conv.seller && conv.seller.email === userEmail) ||
        // Show order messages for buyer if they are the buyer
          (conv && conv.buyer && conv.buyer.email === userEmail)
      ))
    )
  );

  // Build a map of the latest order message per order_id
  const latestOrderMsgById = {};
  filteredMessages.forEach(msg => {
    if (msg.type === 'order' && msg.order_id) {
      if (!latestOrderMsgById[msg.order_id] || (msg.timestamp?.seconds || 0) > (latestOrderMsgById[msg.order_id].timestamp?.seconds || 0)) {
        latestOrderMsgById[msg.order_id] = msg;
      }
    }
  });

  // Build a set of order message IDs to keep (latest per order_id)
  const latestOrderMsgIds = new Set(Object.values(latestOrderMsgById).map(msg => msg.id));

  // Merge all messages, but for order messages, only keep the latest per order_id
  const mergedMessages = filteredMessages.filter(msg => {
    if (msg.type === 'order') {
      return latestOrderMsgIds.has(msg.id);
    }
    return true;
  });

  // Sort all messages by timestamp (oldest to newest)
    return mergedMessages.slice().sort((a, b) => {
    const aTime = a.timestamp?.seconds || 0;
    const bTime = b.timestamp?.seconds || 0;
    return aTime - bTime;
  });
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced message filtering with background processing
  const sortedMergedMessages = useMemo(() => {
    // ⚡️ PERFORMANCE OPTIMIZATION: Use requestIdleCallback for heavy processing only for very large message lists
    if (window.requestIdleCallback && chatMessages.length > 500) {
      // For very large lists, process in background but return immediately with cached result
      requestIdleCallback(() => {
        processMessages(chatMessages, isAdminDashboard, conversation, user?.email);
      });
      // Return cached or processed result immediately
      return processMessages(chatMessages, isAdminDashboard, conversation, user?.email);
    }
    
    return processMessages(chatMessages, isAdminDashboard, conversation, user?.email);
  }, [chatMessages, isAdminDashboard, conversation, user?.email, processMessages]);

  const getTransactionStatusText = (status, category = '') => {
    if (!status) return '';
    const s = status.toLowerCase().replace(/[\s_-]/g, ''); // normalize: remove spaces, hyphens, underscores
    if (s === 'onhold') return 'Your payment is under checking. Once verified, your order will move to processing. No action needed — we\'ll update you soon.';
    if (s === 'processing' || s === 'inprogress') return 'We\'ve received your payment and informed the seller to transfer the account to us. Your order is now being processed.';
    if (s === 'nuwanisuru') {
      // Check if this is a Facebook category order
      if (category.toLowerCase().includes('facebook')) {
        return 'The seller has assigned the escrow agent as a page admin, and the escrow agent will review everything and remove other admins. We will contact you as soon as it is ready to be released.';
      }
      return 'Seller has been transferred account to our escrow agent, who is now reviewing and securing the login details. We\'ll contact you shortly once it\'s ready for release.';
    }
    if (s === 'nuwanis') {
      // Check if this is a Facebook category order
      if (category.toLowerCase().includes('facebook')) {
        return 'The escrow agent has transferred administrative rights to you. Please accept invite and confirm within 24 hours. If we don\'t hear from you within 24 hours, we\'ll consider the order complete and release payment to the seller.';
      }
      return (
      <>
        The account details have been successfully transferred to you.
        Please check the login info box and confirm within 24 hours. If we don't hear from you within 24 hours, we'll consider the order complete and release payment to the seller.
        <br /><br />
        <span className="font-bold text-black">
          Buyer, we will change e-mail, change 2 step verification settings and delete all connected devices. So, we recommend to wait 1/2 days to be sure that nothing happen. Sometimes accounts may get locked due to a lot of security changes
        </span>
      </>
    );
    }
    if (s === 'completed' || s === 'complete') return 'The transaction has been completed successfully! 🎉\nWe\'d really appreciate it if you could take a moment to leave a review about your experience with us.\nYour feedback helps us grow and serve you better!';
    if (s === 'refunded') return 'Dear buyer, your refund has been processed successfully. The amount will reflect in your account within 3–5 business days. Let us know if you need any further assistance.';
    return '';
  };

  // 🎨 ANIMATION: Message bubble animation variants
  const messageVariants = {
    hidden: { 
      opacity: 0, 
      y: 20, 
      scale: 0.95 
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      y: -10, 
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };





  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize message status renderer
  const renderMessageStatus = useCallback((msg) => {
    // Check if the message is from the current user - using same logic as MessageBubble
    const isFromCurrentUser = user && (
      // Check if message is from current user by email
      (msg.from && user.email && msg.from.toLowerCase() === user.email.toLowerCase()) ||
      // Check if message is from current user by UID
      (msg.from && user.uid && msg.from === user.uid) ||
      // Check if message is from current user by fromUid field
      (msg.fromUid && user.uid && msg.fromUid === user.uid) ||
      (msg.fromUid && user.email && msg.fromUid === user.email) ||
      // Check legacy fields
      (msg.senderId && user.uid && msg.senderId === user.uid) ||
      // Admin check
      (msg.isAdmin && user.email === 'ytproudltd@gmail.com')
    );
    if (!isFromCurrentUser) return null;

    // Handle optimistic UI status
    if (msg.status === 'sending') {
      return (
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400 animate-pulse">⏳</span>
        </div>
      );
    } else if (msg.status === 'failed') {
      return (
        <div className="flex items-center gap-1">
          <span className="text-xs text-red-500" title={msg.error || 'Failed to send'}>❌</span>
        </div>
      );
    } else if (msg.status === 'sent') {
      return (
        <div className="flex items-center gap-1">
          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </div>
      );
    } else if (msg.status === 'delivered') {
      return (
        <div className="flex items-center">
          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
          <svg className="w-4 h-4 text-gray-400 -ml-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </div>
      );
    } else if (msg.status === 'read') {
      return (
        <div className="flex items-center">
          <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
          <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-400'} -ml-3`} fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </div>
      );
    } else if (msg.status === 'offline') {
      return (
        <div className="flex items-center gap-1">
          <span className="text-xs text-yellow-500">📤</span>
        </div>
      );
    }

          // For agent chat, show WhatsApp-style ticks
    if (conversation && conversation.id === 'admin') {
      const isReadByAdmin = msg.readByAdmin === true;
      return (
        <div className="flex items-center">
                    {isReadByAdmin ? (
            // Double blue ticks for read messages (WhatsApp style)
            <>
              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
              <svg className="w-4 h-4 text-blue-500 -ml-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </>
          ) : (
            // Single gray tick for sent messages (WhatsApp style)
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          )}
        </div>
      );
    }

    // Determine the other party's email
    let otherPartyEmail = null;
    if (conversation && conversation.buyer && conversation.seller && user) {
      if (conversation.buyer.email === user.email) {
        otherPartyEmail = conversation.seller.email;
      } else if (conversation.seller.email === user.email) {
        otherPartyEmail = conversation.buyer.email;
      }
    }
    const isRead = Array.isArray(msg.readBy) && otherPartyEmail && msg.readBy.includes(otherPartyEmail);

        // ⚡️ WHATSAPP STYLE: Show blue ticks for read, gray for sent
    return (
      <div className="flex items-center">
                {isRead ? (
          // Double blue ticks for read messages (WhatsApp style)
          <>
            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
            <svg className="w-4 h-4 text-blue-500 -ml-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </>
        ) : (
          // Single gray tick for sent messages (WhatsApp style)
          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        )}
      </div>
    );
  }, [user, conversation]);

  // Security: Enhanced function to save encrypted login info with re-authentication
  const handleSaveLoginInfo = async (orderId, loginData = null) => {
    if (!user || !orderId) return;
    
    try {
      // Security: Log login info save attempt
      logSecurityEvent('LOGIN_INFO_SAVE_ATTEMPT', { orderId }, 'info');
      
      // Security: Enhanced re-authentication for sensitive operations
      let reAuthSuccess = false;
      
      try {
        // Check if user needs re-authentication (last auth > 30 minutes ago)
        const lastAuthTime = user.metadata?.lastSignInTime;
        const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
        
        if (lastAuthTime && new Date(lastAuthTime) < thirtyMinutesAgo) {
          // Prompt for re-authentication
          const password = prompt('Please re-enter your password for security:');
          if (!password) {
            throw new Error('Re-authentication required for sensitive operation');
          }
          
          // In a real implementation, you would use Firebase re-authentication
          // For now, we'll simulate the check
          reAuthSuccess = true;
          logSecurityEvent('RE_AUTHENTICATION_SUCCESS', { orderId }, 'info');
        } else {
          reAuthSuccess = true;
        }
      } catch (reAuthError) {
        logSecurityEvent('RE_AUTHENTICATION_FAILED', { 
          orderId, 
          error: reAuthError.message 
        }, 'error');
        
        if (typeof toast !== 'undefined') {
          toast.error('Re-authentication required for this operation');
        } else {
          alert('Re-authentication required for this operation');
        }
        return;
      }
      
      if (!reAuthSuccess) {
        throw new Error('Re-authentication failed');
      }
      
      // Security: Store sensitive login info in separate secured collection
      const loginInfoRef = doc(db, 'loginInfo', `${orderId}_${user.uid}`);
      
      // Security: Create encrypted login info object with enhanced validation
      const sensitiveData = {
        username: loginData?.username || '',
        password: loginData?.password || '',
        email: loginData?.email || '',
        additionalInfo: loginData?.additionalInfo || '',
        // Add integrity hash to prevent tampering
        integrityHash: CryptoJS.SHA256(
          `${loginData?.username || ''}${loginData?.password || ''}${user.uid}${Date.now()}`
        ).toString()
      };
      
      // Security: Encrypt sensitive data with enhanced encryption
      const encryptedData = encryptSensitiveData(sensitiveData);
      if (!encryptedData) {
        throw new Error('Encryption failed');
      }
      
      const loginInfo = {
        orderId,
        ownerUid: user.uid, // Security: Use UID for server-side validation
        ownerEmail: user.email,
        createdAt: serverTimestamp(),
        encryptedData, // ✅ ENCRYPTED
        version: '1.0', // For future encryption key rotation
        lastUpdated: serverTimestamp(),
        // Add metadata for audit trail
        metadata: {
          encryptedAt: new Date().toISOString(),
          encryptionMethod: 'AES-256',
          dataIntegrity: true
        }
      };
      
      await setDoc(loginInfoRef, loginInfo);
      
      // Security: Log successful save with minimal sensitive data
      logSecurityEvent('LOGIN_INFO_SAVED', { 
        orderId, 
        hasData: !!loginData,
        dataLength: Object.keys(sensitiveData).length 
      }, 'info');
      
      if (typeof toast !== 'undefined') {
        toast.success('Login information saved securely');
      }
    } catch (error) {
      // Security: Log error without exposing sensitive details
      logSecurityEvent('LOGIN_INFO_SAVE_ERROR', { 
        orderId, 
        errorType: error.name,
        errorMessage: error.message 
      }, 'error');
      
      if (typeof toast !== 'undefined') {
        toast.error('Failed to save login information');
      }
    }
  };

  // Join the room when a conversation is selected


  // Security: Listen for new messages via socket with validation and deduplication
  useEffect(() => {
    const handleSocketMessage = (msg) => {
      // Security: Validate and sanitize socket messages
      const validatedMsg = validateSocketMessage(msg);
      if (!validatedMsg) {
        logSecurityEvent('REJECTED_SOCKET_MESSAGE', { originalMsg: msg }, 'warning');
        return;
      }
      
      // Security: Check if message belongs to current conversation
      if (conversation && conversation.id && validatedMsg.roomId !== conversation.id) {
        logSecurityEvent('MESSAGE_ROOM_MISMATCH', { 
          messageRoomId: validatedMsg.roomId, 
          currentRoomId: conversation.id 
        }, 'warning');
        return;
      }
      
      // ⚡️ SERVER SYNC: Check if this is a confirmation of our pending message
      if (validatedMsg.tempId && pendingMessages.some(pending => pending.id === validatedMsg.tempId)) {
        // ⚡️ SERVER ACKNOWLEDGMENT: Update pending message with server confirmation
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGE', 
          payload: { 
            id: validatedMsg.tempId, 
            status: 'sent',
            isConfirmed: true,
            serverId: validatedMsg.id
          } 
        });
        return;
      }
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Deduplicate socket messages
      if (!deduplicateMessage(validatedMsg)) {
        console.log('Duplicate socket message ignored');
        return;
      }
      
      dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [...firestoreMessages, validatedMsg] });
    };
    const unsubscribe = onMessage(handleSocketMessage);
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [onMessage, conversation, deduplicateMessage]);

  // Handle optimistic UI updates when Firestore messages come back
  useEffect(() => {
    // When new messages come from Firestore, remove matched pending messages
    const updateOptimisticMessages = () => {
      dispatch({ 
        type: 'UPDATE_PENDING_MESSAGES', 
        payload: pendingMessages.filter(pending => {
          // Remove pending messages that have been confirmed by Firestore
          const isConfirmed = firestoreMessages.some(firestore => 
            firestore.tempId && firestore.tempId === pending.id
          );
          return !isConfirmed;
        })
      });
    };

    updateOptimisticMessages();
  }, [firestoreMessages]);

  return (
          <section className={`flex-1 flex flex-col h-full ${
            isDarkMode ? 'bg-gradient-to-br from-[#0f0f0f] to-[#0f0f0f]' : 'bg-gradient-to-br from-gray-50 to-gray-100'
          }`}>
      {/* Header - Only show when conversation is selected */}
      {conversation && (
          <div className="relative">
          {/* Offline Indicator */}
          {!isOnline && (
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-medium z-10 shadow-lg">
              🔴 Offline - Messages will be sent when you're back online
          </div>
          )}
          <div className={`flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 mb-2 mt-2 sm:mt-4 shadow-lg rounded-2xl sm:rounded-3xl mx-2 sm:mx-4 border ${
            isDarkMode ? 'bg-[#2a2a2a] border-gray-700/50' : 'bg-white border-gray-200'
          }`}>
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Back Arrow - Only show on mobile/tablet */}
          <button 
            onClick={() => {
              onSelectConversation(null);
              // Navigate to messages page without room parameter
              window.history.pushState({}, '', '/messages');
            }}
            className={`xl:hidden p-1.5 sm:p-2 rounded-full transition-colors mr-1 sm:mr-2 ${
              isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'
            }`}
            aria-label="Back to conversations"
          >
            <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="relative">
            <img src={otherPartyAvatar || conversation?.avatar} alt={otherPartyName || conversation?.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full" />
            <span className={`absolute bottom-0.5 sm:bottom-1 right-0.5 sm:right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full border-2 border-white ${otherPartyOnline ? 'bg-green-500' : 'bg-gray-400'}`}></span>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-semibold text-sm sm:text-base truncate ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {otherPartyName ? `${otherPartyName} , YTproud Agent` : (conversation?.name ? `${conversation?.name} , YTproud Agent` : 'Unknown User , YTproud Agent')}
            </div>
            <div className={`text-xs ${otherPartyOnline ? 'text-green-400' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>{otherPartyOnline ? 'Online' : 'Offline'}</div>
          </div>
        </div>
        {!conversation?.isAdmin && (
          <button 
            onClick={() => dispatch({ type: 'SET_SHOW_PRODUCT_MODAL', payload: true })}
            className="btn btn-primary bg-red-500 hover:bg-red-600 rounded-full px-3 sm:px-6 py-1.5 sm:py-2 font-semibold shadow text-xs sm:text-sm"
          >
            Product
          </button>
        )}
          </div>
      </div>
      )}
      {/* Chat Messages (including order details card at the top) */}
      <div 
        className={`flex-1 min-h-0 px-3 sm:px-6 py-2 overflow-y-auto flex flex-col gap-3 sm:gap-4 custom-scrollbar relative ${isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : ''}`}
        ref={chatContainerRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag & Drop Overlay */}
        {isDragOver && (
          <div className="absolute inset-0 bg-blue-50 bg-opacity-90 flex items-center justify-center z-10 rounded-lg">
            <div className="text-center">
              <div className="text-4xl mb-2">📁</div>
              <div className="text-lg font-semibold text-blue-600">Drop your file here</div>
              <div className="text-sm text-blue-500">Images and videos supported</div>
            </div>
          </div>
        )}

        {/* Loading indicator for older messages */}
        {loadingMore && (
          <div className="flex justify-center items-center py-2">
            <span className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-red-500"></span>
            <span className="ml-2 text-sm text-gray-500">Loading older messages...</span>
          </div>
        )}
        
        {/* --- NEW: Show order/escrow/status boxes if transaction conversation and no order message --- */}
        {conversation && conversation.type === 'transaction' && !sortedMergedMessages.some(m => m.type === 'order') && (
          <div className={`mb-4 w-full rounded-2xl shadow-lg border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 px-6 py-4 ${
            isDarkMode ? 'bg-gradient-to-br from-[#0f0f0f] to-[#0f0f0f] border-gray-700/50' : 'bg-white border-gray-200'
          }`}>
            {/* Order Info Row */}
            <div className="w-full mb-4">
              <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="12" fill="#FF0000"/>
                    <polygon points="10,8 16,12 10,16" fill="white"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <div className={`font-bold text-xl ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>YTproud Marketplace</div>
                  <div className={`text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>Order Details</div>
                </div>
                <div className={`text-sm px-4 py-2 rounded-xl ${
                  isDarkMode ? 'text-gray-300 bg-[#1a1a1a]' : 'text-gray-700 bg-gray-100'
                }`}>{conversation.order_date || ''}</div>
              </div>
              <div className="px-6 py-6 mb-4 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-sm flex flex-wrap gap-6 items-center justify-between">
                {/* Order ID */}
                <div className="flex items-center gap-3 min-w-[150px]">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </span>
                  <div>
                    <div className="text-sm text-gray-400">Order ID</div>
                    <div className="font-semibold text-white">{conversation.order_id || 'N/A'}</div>
                  </div>
                </div>
                {/* Product Name */}
                <div className="flex items-center gap-3 min-w-[150px]">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                  </span>
                  <div>
                    <div className="text-sm text-gray-500">Product</div>
                    <div className="font-semibold text-gray-800">{conversation.product_name || 'N/A'}</div>
                  </div>
                </div>
                {/* Category */}
                <div className="flex items-center gap-3 min-w-[150px]">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 text-white shadow-md">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                  </span>
                  <div>
                    <div className="text-sm text-gray-500">Category</div>
                    <div className="font-semibold text-gray-800">{conversation.category_name || 'Uncategorized'}</div>
                  </div>
                </div>
                {/* Payment */}
                <div className="flex items-center gap-3 min-w-[150px]">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/></svg>
                  </span>
                  <div>
                    <div className="text-sm text-gray-500">Payment</div>
                    <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-base">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#22c55e"/><path d="M8 12l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Paid
                    </span>
                  </div>
                </div>
                {/* Amount */}
                <div className="flex items-center gap-3 min-w-[150px]">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-md">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  <div>
                    <div className="text-sm text-gray-500">Amount</div>
                    <span className="text-gray-800 font-semibold text-base">
                      ${conversation.product_price || conversation.order_total || '0.00'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* How Escrow Works Box */}
            <div className="w-full">
              <div className="bg-gradient-to-br from-red-50 via-white to-gray-50 border border-red-100 rounded-2xl shadow-xl p-8 mt-4 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-md">
                    <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/></svg>
                  </div>
                  <span className="font-extrabold text-2xl text-gray-800 tracking-tight">How Escrow Works</span>
                </div>
                {(() => {
                  const category = (conversation.category_name || '').toLowerCase();
                  const stepCircle = (n) => (
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white font-bold text-lg shadow">{n}</span>
                  );
                  if (category.includes('youtube')) {
                    return (
                      <ol className="space-y-5 pl-2">
                        <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller to add the agent's email address as the owner.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">The seller designates the escrow agent as owner.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">The escrow agent verifies everything and assigns manager rights to the buyer.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">After 7 days, the escrow agent remove all the other managers and assigns primary ownership rights to the buyer <span className="italic text-gray-500">(7 days is the minimum amount of time required in order to assign a new primary owner in the control panel.)</span></span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After completing the transaction, the money is sent by the agent to the seller</span></li>
                      </ol>
                    );
                  } else if (category.includes('facebook')) {
                    return (
                      <ol className="space-y-5 pl-2">
                        <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller assigns the escrow agent's page as the administrator.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">After the seller assigns the Escrow agent as a page administrator, the Escrow agent checks everything and removes other administrators.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">Then, Escrow agent adds buyer Facebook account as administrator.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">The buyer must check and confirm it with the agent after logging into the Facebook page.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After confirming the buyer and then remove the agent's account and completing the transaction, the money will be sent by the agent to the seller.</span></li>
                      </ol>
                    );
                  } else if (category.includes('instagram')) {
                    return (
                      <ol className="space-y-5 pl-2">
                        <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller to transfers login details and the original email account to the escrow agent.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">The escrow agent checks everything, changes the login details.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">After that ,the login details are sent to the buyer by the agent.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">After the buyer has logged into the account, checking the account and confirming it with the agent</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After completing the transaction, the money is sent by the agent to the seller</span></li>
                      </ol>
                    );
                  } else if (category.includes('tiktok')) {
                    return (
                      <ol className="space-y-5 pl-2">
                        <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller to transfers login details and the original email account to the escrow agent.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">The escrow agent checks everything, changes the login details.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">After that ,the login details are sent to the buyer by the agent.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">After the buyer has logged into the account, checking the account and confirming it with the agent</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After completing the transaction, the money is sent by the agent to the seller</span></li>
                      </ol>
                    );
                  } else {
                    // Default: YouTube steps
                    return (
                      <ol className="space-y-5 pl-2">
                        <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller to add the agent's email address as the owner.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">The seller designates the escrow agent as owner.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">The escrow agent verifies everything and assigns manager rights to the buyer.</span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">After 7 days, the escrow agent remove all the other managers and assigns primary ownership rights to the buyer <span className="italic text-gray-500">(7 days is the minimum amount of time required in order to assign a new primary owner in the control panel.)</span></span></li>
                        <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After completing the transaction, the money is sent by the agent to the seller</span></li>
                      </ol>
                    );
                  }
                })()}
              </div>
            </div>
            {/* Transaction Status Box */}
            <div className="w-full mt-4">
              <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg p-6 transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-bold text-lg text-gray-800 flex items-center gap-2">
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-md">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <span>Transaction Status</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-base text-black">{conversation.status ? (conversation.status.charAt(0).toUpperCase() + conversation.status.slice(1)) : 'On Hold'}</span>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                  <div className="text-gray-700 text-base leading-relaxed">
                    {getTransactionStatusText(conversation.status, conversation.category_name)}
                  </div>
                </div>
                {/* Status Timeline */}
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    {(() => {
                      // Define the steps in order
                      const steps = [
                        { key: 'onhold', label: 'On Hold', icon: (
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        ) },
                        { key: 'processing', label: 'Processing', icon: (
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        ) },
                        { key: 'nuwanis', label: 'Ready', icon: (
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        ) },
                        { key: 'completed', label: 'Complete', icon: (
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        ) },
                      ];
                      // Normalize status
                      const s = (conversation.status || '').toLowerCase().replace(/[-_ ]/g, '');
                      // Find current step index
                      let currentIdx = steps.findIndex(step => {
                        if (s === 'nuwanisuru' && step.key === 'processing') return true;
                        if (s === 'nuwanis' && step.key === 'nuwanis') return true;
                        if (s === 'complete' && step.key === 'completed') return true;
                        return s === step.key;
                      });
                      if (currentIdx === -1) currentIdx = 0;
                      return steps.map((step, idx) => (
                        <React.Fragment key={step.key}>
                          <div className={`flex flex-col items-center`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${idx < currentIdx ? 'bg-red-500' : idx === currentIdx ? 'bg-green-500' : 'bg-gray-200'}`}>
                              {idx < currentIdx ? (
                                // Red check mark for passed steps
                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                                  <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              ) : (
                                // Original icon for current and future steps
                                step.icon
                              )}
                            </div>
                            <span className="text-xs mt-1 text-gray-500">{step.label}</span>
                          </div>
                          {idx < steps.length - 1 && (
                            <div className="flex-1 h-1 mx-2 bg-gray-200">
                              <div className={`h-full ${idx < currentIdx ? 'bg-green-500' : ''}`}></div>
                            </div>
                          )}
                        </React.Fragment>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>
            {/* Login Info Box (not editable here, just show if present) */}
            {conversation.login_info && (
              <div className="w-full mt-4">
                <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl shadow p-5">
                  <div className="font-bold text-base text-gray-800 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                      <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Login Info Box
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <div className="text-gray-700 whitespace-pre-wrap font-mono text-sm">
                      {conversation.login_info}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Messages */}
        {!conversation ? (
          <div className="hidden sm:flex flex-col items-center justify-center h-full px-4">
            {/* Empty state - no conversation selected */}
          </div>
        ) : sortedMergedMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center mt-10">
            <div className="text-2xl font-bold text-gray-700 mb-2">Welcome!</div>
            <div className="text-base text-gray-500 text-center max-w-md">
              {(() => {
                if (user && conversation && conversation.buyer && conversation.seller) {
                  if (conversation.buyer.email && conversation.buyer.email.toLowerCase() === user.email.toLowerCase()) {
                    return 'Your conversation has started 🎉 You can now start chatting with the seller.';
                  } else if (conversation.seller.email && conversation.seller.email.toLowerCase() === user.email.toLowerCase()) {
                    return 'Your conversation has started 🎉 You can now start chatting with the buyer.';
                  }
                }
                return 'Your conversation has started 🎉 You can now start chatting.';
              })()}
            </div>
          </div>
        ) : (
          // ⚡️ PERFORMANCE OPTIMIZATION: Show pending messages immediately + lazy loaded messages
          <>
            {/* ⚡️ INSTANT DISPLAY: All messages (pending + existing) with virtualization and animations */}
            {allVisibleMessages.length > 100 ? (
              <VirtualizedMessageList
                messages={allVisibleMessages}
                user={user}
                isAdminDashboard={isAdminDashboard}
                conversation={conversation}
                onImageClick={handleImageClick}
                renderMessageStatus={renderMessageStatus}
                containerHeight={600}
                itemHeight={80}
              />
            ) : (
              <>
                {allVisibleMessages.map((msg, idx) => {
            if (msg.type === 'order') {
              // Render the order card using only the message's own data
              const statusRaw = msg.status || '';
              const status = statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1).toLowerCase();
              const orderId = msg.order_id || msg.orderId || '';
              const productName = msg.product_name || msg.productName || 'Product';
              const orderDate = msg.order_date || msg.date || '';
              return (
                <div key={msg.id || idx} className="mb-4 w-full bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 px-6 py-4">
                  {/* Order Info Row */}
                  <div className="w-full mb-4">
                    <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-700/50">
                      <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="12" fill="#FF0000"/>
                          <polygon points="10,8 16,12 10,16" fill="white"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-xl text-gray-900">YTproud Marketplace</div>
                        <div className="text-sm text-gray-500">Order Details</div>
                      </div>
                      <div className="text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-xl">{orderDate ? orderDate : ''}</div>
                    </div>
                                         <div className={`px-6 py-6 mb-4 rounded-2xl shadow-sm flex flex-wrap gap-6 items-center justify-between ${
                                           isDarkMode ? 'bg-gradient-to-br from-[#0f0f0f] to-[#0f0f0f]' : 'bg-gradient-to-br from-gray-50 to-white'
                                         }`}>
                      {/* Order ID */}
                      <div className="flex items-center gap-3 min-w-[150px]">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md">
                          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                        </span>
                        <div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Order ID</div>
                          <div className={`font-semibold flex items-center gap-2 ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-800'
                          }`}>
                            {orderId}
                          </div>
                        </div>
                      </div>
                      {/* Product */}
                      <div className="flex items-center gap-3 min-w-[150px]">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md">
                          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        </span>
                        <div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Product</div>
                          <div className={`font-semibold flex items-center gap-2 ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-800'
                          }`}>
                            <img src={msg.product_image || 'https://img.icons8.com/color/48/000000/youtube-play.png'} alt="Product" className="w-8 h-8 rounded-lg shadow border" />
                            {productName}
                          </div>
                        </div>
                      </div>
                      {/* Category */}
                      <div className="flex items-center gap-3 min-w-[150px]">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-md">
                          {(() => {
                            const category = (msg.category_name || msg.product_category || '').toLowerCase();
                            if (category.includes('youtube')) {
                              return (
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                              );
                            } else if (category.includes('tiktok')) {
                              return (
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                </svg>
                              );
                            } else if (category.includes('facebook')) {
                              return (
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                              );
                            } else if (category.includes('instagram')) {
                              return (
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                                </svg>
                              );
                            } else {
                              return (
                                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24">
                                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                              );
                            }
                          })()}
                        </span>
                        <div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Category</div>
                          <div className={`font-semibold ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-800'
                          }`}>
                            {msg.category_name || 'Uncategorized'}
                          </div>
                        </div>
                      </div>
                      {/* Payment */}
                      <div className="flex items-center gap-3 min-w-[150px]">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md">
                          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/></svg>
                        </span>
                        <div>
                          <div className="text-sm text-gray-500">Payment</div>
                          {(() => {
                            const s = (statusRaw || '').toLowerCase().replace(/[-_ ]/g, '');
                            if (s === 'onhold') {
                              return (
                                <span className="font-semibold text-base text-black">Checking</span>
                              );
                            }
                            if (s === 'refunded') {
                              return (
                                <span className="inline-flex items-center gap-1 text-blue-600 font-semibold text-base">
                                  {/* Refund/Rotate Icon */}
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  Refund
                                </span>
                              );
                            }
                            // Default: Paid
                            return (
                              <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-base">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#22c55e"/><path d="M8 12l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Paid
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      {/* Amount */}
                      <div className="flex items-center gap-3 min-w-[150px]">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-md">
                          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </span>
                        <div>
                          <div className="text-sm text-gray-500">Amount</div>
                          <span className="text-gray-800 font-semibold text-base">
                            ${msg.product_price || msg.amount || '0.00'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* How Escrow Works Box */}
                  <div className="w-full">
                    <div className="bg-gradient-to-br from-red-50 via-white to-gray-50 border border-red-100 rounded-2xl shadow-xl p-8 mt-4 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-md">
                          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/></svg>
                        </div>
                        <span className="font-extrabold text-2xl text-gray-800 tracking-tight">How Escrow Works</span>
                      </div>
                      {(() => {
                        const category = (msg.category_name || msg.product_category || '').toLowerCase();
                        const stepCircle = (n) => (
                          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white font-bold text-lg shadow">{n}</span>
                        );
                        if (category.includes('youtube')) {
                          return (
                            <ol className="space-y-5 pl-2">
                              <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller to add the agent's email address as the owner.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">The seller designates the escrow agent as owner.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">The escrow agent verifies everything and assigns manager rights to the buyer.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">After 7 days, the escrow agent remove all the other managers and assigns primary ownership rights to the buyer <span className="italic text-gray-500">(7 days is the minimum amount of time required in order to assign a new primary owner in the control panel.)</span></span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After completing the transaction, the money is sent by the agent to the seller</span></li>
                            </ol>
                          );
                        } else if (category.includes('facebook')) {
                          return (
                            <ol className="space-y-5 pl-2">
                              <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller assigns the escrow agent's page as the administrator.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">After the seller assigns the Escrow agent as a page administrator, the Escrow agent checks everything and removes other administrators.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">Then, Escrow agent adds buyer Facebook account as administrator.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">The buyer must check and confirm it with the agent after logging into the Facebook page.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After confirming the buyer and then remove the agent's account and completing the transaction, the money will be sent by the agent to the seller.</span></li>
                            </ol>
                          );
                        } else if (category.includes('instagram')) {
                          return (
                            <ol className="space-y-5 pl-2">
                              <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller to transfers login details and the original email account to the escrow agent.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">The escrow agent checks everything, changes the login details.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">After that ,the login details are sent to the buyer by the agent.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">After the buyer has logged into the account, checking the account and confirming it with the agent</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After completing the transaction, the money is sent by the agent to the seller</span></li>
                            </ol>
                          );
                        } else if (category.includes('tiktok')) {
                          return (
                            <ol className="space-y-5 pl-2">
                              <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller to transfers login details and the original email account to the escrow agent.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">The escrow agent checks everything, changes the login details.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">After that ,the login details are sent to the buyer by the agent.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">After the buyer has logged into the account, checking the account and confirming it with the agent</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After completing the transaction, the money is sent by the agent to the seller</span></li>
                            </ol>
                          );
                        } else {
                          // Default: YouTube steps
                          return (
                            <ol className="space-y-5 pl-2">
                              <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className="text-gray-700 text-base font-medium">After the buyer make a purchase , the agent will notify the seller to add the agent's email address as the owner.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className="text-gray-700 text-base font-medium">The seller designates the escrow agent as owner.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className="text-gray-700 text-base font-medium">The escrow agent verifies everything and assigns manager rights to the buyer.</span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className="text-gray-700 text-base font-medium">After 7 days, the escrow agent remove all the other managers and assigns primary ownership rights to the buyer <span className="italic text-gray-500">(7 days is the minimum amount of time required in order to assign a new primary owner in the control panel.)</span></span></li>
                              <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className="text-gray-700 text-base font-medium">After completing the transaction, the money is sent by the agent to the seller</span></li>
                            </ol>
                          );
                        }
                      })()}
                    </div>
                  </div>
                  {/* Transaction Status Box */}
                  <div className="w-full mt-4">
                    <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg p-6 transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                      <div className="flex items-center justify-between mb-4">
                        <div className="font-bold text-lg text-gray-800 flex items-center gap-2">
                          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-md">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                              <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                      </div>
                          <span>Transaction Status</span>
                      </div>
                        <div className="flex items-center gap-2">
                          {/* Remove button background and text for Paid. Only show text for Checking and Refund. */}
                          {(() => {
                            const s = (statusRaw || '').toLowerCase().replace(/[-_ ]/g, '');
                            if (s === 'onhold') return <span className="font-semibold text-base text-black">Checking</span>;
                            if (s === 'refunded') return <span className="font-semibold text-base text-blue-600">Refund</span>;
                            // For Paid statuses, show nothing
                            return null;
                          })()}
                        </div>
                  </div>
                      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                        <div className="text-gray-700 text-base leading-relaxed">
                          {getTransactionStatusText(statusRaw, msg.category_name || msg.product_category)}
                        </div>
                      </div>
                      {/* Status Timeline */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between">
                          {(() => {
                            // Define the steps in order
                            const steps = [
                              { key: 'onhold', label: 'On Hold', icon: (
                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                              ) },
                              { key: 'processing', label: 'Processing', icon: (
                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                                  <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                              ) },
                              { key: 'nuwanis', label: 'Ready', icon: (
                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                              ) },
                              { key: 'completed', label: 'Complete', icon: (
                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                              ) },
                            ];
                            // Normalize status
                            const s = (statusRaw || '').toLowerCase().replace(/[-_ ]/g, '');
                            // Find current step index
                            let currentIdx = steps.findIndex(step => {
                              if (s === 'nuwanisuru' && step.key === 'processing') return true;
                              if (s === 'nuwanis' && step.key === 'nuwanis') return true;
                              if (s === 'complete' && step.key === 'completed') return true;
                              return s === step.key;
                            });
                            if (currentIdx === -1) currentIdx = 0;
                            return steps.map((step, idx) => (
                              <React.Fragment key={step.key}>
                                <div className="flex flex-col items-center">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${idx < currentIdx ? 'bg-red-500' : idx === currentIdx ? 'bg-green-500' : 'bg-gray-200'}`}> 
                                    {idx < currentIdx ? (
                                      // Red check mark for passed steps
                                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                                        <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    ) : (
                                      // Original icon for current and future steps
                                      step.icon
                                    )}
                                  </div>
                                  <span className="text-xs mt-1 text-gray-500">{step.label}</span>
                                </div>
                                {idx < steps.length - 1 && (
                                  <div className="flex-1 h-1 mx-2 bg-gray-200">
                                    <div className={`h-full ${idx < currentIdx ? 'bg-green-500' : ''}`}></div>
                                  </div>
                                )}
                              </React.Fragment>
                            ));
                          })()}
                        </div>
                      </div>
                      {/* Login Info Box */}
                      {msg.type === 'order' && (
                        // Admin: show for nuwanisuru, nuwanis, completed
                        (isAdminDashboard && ['nuwanisuru', 'nuwanis', 'completed'].includes(statusRaw.toLowerCase())) ? (
                          <div className="w-full mt-4">
                            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl shadow p-5">
                              <div className="font-bold text-base text-gray-800 mb-3 flex items-center gap-2">
                                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                                  <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Login Info Box
                              </div>
                              <div className="space-y-3">
                                {/* Only allow editing for nuwanisuru */}
                                {['nuwanisuru', 'nuwanis', 'completed'].includes(statusRaw.toLowerCase()) && (
                                  <>
                                    <textarea
                                      value={loginInfo}
                                      onChange={(e) => dispatch({ type: 'SET_LOGIN_INFO', payload: e.target.value })}
                                      placeholder="Enter login information here..."
                                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 min-h-[100px] resize-none"
                                    />
                                    <button
                                      onClick={async () => {
                                        await handleSaveLoginInfo(msg.id);
                                        dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: firestoreMessages.map(m => m.id === msg.id ? { ...m, login_info: loginInfo } : m) });
                                        dispatch({ type: 'SET_LOGIN_INFO', payload: '' });
                                      }}
                                      disabled={savingLoginInfo || !loginInfo.trim()}
                                      className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                    >
                                      {savingLoginInfo ? (
                                        <span className="flex items-center justify-center">
                                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                          Saving...
                                        </span>
                                      ) : (
                                        'Save Login Info'
                                      )}
                                    </button>
                                  </>
                                )}
                                {/* Always show saved info if present */}
                                {msg.login_info && (
                                  <div className="bg-white rounded-xl p-4 border border-gray-200 mt-2">
                                    <div className="text-gray-700 whitespace-pre-wrap font-mono text-sm">
                                      {msg.login_info}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Buyer: show for nuwanis, completed
                          (conversation.buyer && conversation.buyer.email === user?.email && ['nuwanis', 'completed'].includes(statusRaw.toLowerCase()) && msg.login_info) && (
                            <div className="w-full mt-4">
                              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl shadow p-5">
                                <div className="font-bold text-base text-gray-800 mb-3 flex items-center gap-2">
                                  <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  Login Info Box
                                </div>
                                <div className="bg-white rounded-xl p-4 border border-gray-200">
                                  <div className="text-gray-700 whitespace-pre-wrap font-mono text-sm">
                                    {msg.login_info}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        )
                      )}
                    </div>
                  </div>
                  
                  {/* Add Countdown Timer for nuwanis and completed status */}
                  {((statusRaw.toLowerCase() === 'nuwanis' || statusRaw.toLowerCase() === 'completed') && 
                    (() => {
                      const category = (msg.category_name || msg.product_category || '').toLowerCase();
                      return category.includes('instagram') || 
                             category.includes('facebook') || 
                             category.includes('tiktok') ||
                             category.includes('youtube');
                    })()
                  ) && (
                    <CountdownTimer 
                      startTime={msg.timestamp?.toDate() || new Date()} 
                      orderId={msg.order_id || msg.orderId}
                      conversationId={conversation.id}
                      isBuyer={conversation.buyer && conversation.buyer.email === user?.email}
                      status={statusRaw.toLowerCase()}
                      category={msg.category_name || msg.product_category}
                      onStatusUpdate={(newStatus) => {
                        // Update local state if needed
                        dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: firestoreMessages.map(m => 
                            m.id === msg.id ? { ...m, status: newStatus } : m
                        ) });
                      }}
                    />
                  )}
                </div>
              );
            }
            // ⚡️ PERFORMANCE OPTIMIZATION: Use memoized MessageBubble component
            return (
              <MessageBubble
                key={msg.id || idx}
                msg={msg}
                user={user}
                isAdminDashboard={isAdminDashboard}
                conversation={conversation}
                onImageClick={handleImageClick}
                renderMessageStatus={renderMessageStatus}
                onReplyClick={handleReplyToMessage}
                replyToMessage={msg.replyTo}
                onContextMenu={showContextMenu}
              />
            );
          })}
                </>
              )}
            </>
            )}

        {/* Loading More Messages Indicator */}
        {loadingMoreLazy && (
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
              <span className="text-sm text-gray-600">Loading more messages...</span>
            </div>
          </div>
        )}

        {/* WhatsApp-style Upload Progress Message */}
        {uploading && uploadingFile && (
          <div className="flex justify-end mb-4">
            <div className="max-w-xs sm:max-w-sm bg-red-500 text-white rounded-2xl rounded-br-md px-4 py-3 relative overflow-hidden">
              {/* File Preview */}
              <div className="relative mb-3">
                {uploadingFile.type.startsWith('image/') ? (
                  <img 
                    src={uploadingFile.previewUrl} 
                    alt="Uploading preview" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ) : uploadingFile.type.startsWith('video/') ? (
                  <video 
                    src={uploadingFile.previewUrl} 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ) : null}
                
                {/* Upload Progress Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                  <div className="text-center">
                    {/* Circular Progress */}
                    <div className="relative w-16 h-16 mx-auto mb-2">
                      <svg width="64" height="64" viewBox="0 0 64 64">
                <circle
                          cx="32" 
                          cy="32" 
                          r="28" 
                          stroke="rgba(255,255,255,0.3)" 
                          strokeWidth="4" 
                          fill="none" 
                        />
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="#10B981"
                          strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 28}
                          strokeDashoffset={2 * Math.PI * 28 * (1 - uploadProgress / 100)}
                  style={{ transition: 'stroke-dashoffset 0.2s linear' }}
                />
              </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">{Math.round(uploadProgress)}%</span>
              </div>
            </div>
                    <div className="text-white text-sm font-medium">Uploading...</div>
                  </div>
                </div>
              </div>
              
              {/* File Info */}
              <div className="text-sm font-medium truncate">{uploadingFile.name}</div>
              <div className="text-xs text-red-100 opacity-80">
                {Math.round(uploadingFile.size / 1024)} KB • {uploadingFile.type.split('/')[1].toUpperCase()}
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-red-400 rounded-full h-1 mt-2">
                <div 
                  className="bg-white h-1 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
        
        {/* 🎨 WHATSAPP-STYLE: Scroll to bottom button */}
        {!isNearBottom && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={() => {
              scrollToBottom('smooth');
              setShouldAutoScroll(true);
            }}
            className="fixed bottom-24 right-6 z-40 w-12 h-12 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
            aria-label="Scroll to bottom"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </motion.button>
        )}
      </div>

      {/* 🎨 CONTEXT MENU: Render context menu */}
      <MessageContextMenu
        message={contextMenu.message}
        isVisible={contextMenu.isVisible}
        position={contextMenu.position}
        onClose={closeContextMenu}
        onReply={handleContextMenuAction.bind(null, 'reply')}
        onForward={handleContextMenuAction.bind(null, 'forward')}
        onStar={handleContextMenuAction.bind(null, 'star')}
        onPin={handleContextMenuAction.bind(null, 'pin')}
        onAddToFavorites={handleContextMenuAction.bind(null, 'favorite')}
        onDelete={handleContextMenuAction.bind(null, 'delete')}
        onSelect={handleContextMenuAction.bind(null, 'select')}
        onShare={handleContextMenuAction.bind(null, 'share')}
        onReact={handleContextMenuAction.bind(null, 'react')}
      />

      {/* Enhanced Offline Indicator */}
      {!isOnline && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mx-4 mb-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>You're offline</strong> - Messages will be sent when you're back online
                {offlineQueue.length > 0 && (
                  <span className="ml-1">({offlineQueue.length} queued)</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ⚡️ PERFORMANCE OPTIMIZATION: Use memoized InputArea component */}
      {conversation && (
        <InputArea
          user={user}
          conversation={conversation}
          onSendMessage={handleSendMessage}
          onFileUpload={handleFileUpload}
          sending={sending}
          uploading={uploading}
          uploadProgress={uploadProgress}
          uploadingFile={uploadingFile}
          isOnline={isOnline}
          offlineQueue={offlineQueue}
          replyToMessage={replyToMessage}
          onCancelReply={handleCancelReply}
        />
      )}
      {openedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
          onClick={() => dispatch({ type: 'SET_OPENED_IMAGE', payload: null })}
          style={{ cursor: 'zoom-out' }}
        >
          <div className="relative" onClick={e => e.stopPropagation()}>
            <img src={openedImage} alt="Preview" className="max-w-[90vw] max-h-[80vh] rounded-2xl shadow-2xl border-4 border-white" />
            <button
              onClick={() => dispatch({ type: 'SET_OPENED_IMAGE', payload: null })}
              className="absolute top-2 right-2 bg-black bg-opacity-80 rounded-full p-2 hover:bg-opacity-100 transition"
              style={{ zIndex: 10 }}
            >
              <FaTimes size={24} className="text-gray-100" />
            </button>
          </div>
        </div>
      )}
      {/* Product Modal */}
      <ProductModal 
        conversation={conversation}
        isOpen={showProductModal}
        onClose={() => dispatch({ type: 'SET_SHOW_PRODUCT_MODAL', payload: false })}
      />
      
      {/* Security Monitor */}
      <SecurityMonitor />
    </section>
  );
} 